import * as THREE from "three";
import { createSuntraceAuthoredEnvironment } from "./authoredEnvironment";
import type { SuntraceAuthoredDensity } from "./authoredEnvironment";
import { createSuntraceEnvironmentDecals } from "./environmentDecals";
import type { SuntraceColorGrade } from "./colorScript";
import type { SuntraceEnvironmentAssets } from "./environmentAssets";
import {
  applySuntraceShadowProfile,
  createSuntraceShadowProfile,
  snapSuntraceShadowAnchor,
  type SuntraceShadowProfile,
} from "./shadows";
import type { SuntraceQuality } from "./types";
import type { SuntraceMaterialLibrary } from "./materials";
import type { SuntraceTrack, TrackFrame } from "./track";

const createRandom = (seed: number): (() => number) => {
  let state = seed >>> 0;
  return () => {
    state = (state * 1103515245 + 12345) >>> 0;
    return state / 4_294_967_296;
  };
};

const getIslandSurfaceHeight = (x: number, z: number): number => {
  const normalizedX = x / 1.07;
  const normalizedZ = z / 0.91;
  const radius = Math.hypot(normalizedX, normalizedZ);
  const radialFactor = radius / 388;
  const angle = Math.atan2(normalizedZ, normalizedX);
  const hill =
    Math.pow(Math.max(0, 1 - radialFactor), 1.9) * 82 +
    Math.sin(x * 0.024) *
      Math.sin(z * 0.021) *
      7 *
      Math.max(0, 1 - radialFactor);
  const shelf = 8.5 + Math.sin(angle * 4) * 2.2;
  const coast = THREE.MathUtils.smoothstep(radialFactor, 0.84, 1);
  return THREE.MathUtils.lerp(Math.max(shelf, hill), -4.5, coast);
};

const KARST_ARCHETYPES = [
  { profile: [0.72, 0.88, 0.94, 0.86, 0.96, 0.77, 0.84, 0.67, 0.75, 0.57, 0.64, 0.51, 0.47, 0.37, 0.3], phase: 0.7, leanX: 0.12, leanZ: -0.08, crownBreak: 0.22 },
  { profile: [0.78, 0.96, 0.84, 0.92, 0.73, 0.8, 0.62, 0.71, 0.55, 0.63, 0.45, 0.52, 0.38, 0.31, 0.22], phase: 1.6, leanX: -0.16, leanZ: 0.1, crownBreak: 0.34 },
  { profile: [0.67, 0.83, 0.96, 0.81, 0.89, 0.71, 0.81, 0.65, 0.7, 0.57, 0.62, 0.52, 0.48, 0.4, 0.33], phase: 2.5, leanX: 0.08, leanZ: 0.14, crownBreak: 0.18 },
  { profile: [0.75, 0.99, 1, 0.9, 0.97, 0.83, 0.89, 0.74, 0.8, 0.67, 0.73, 0.62, 0.58, 0.49, 0.41], phase: 3.4, leanX: -0.07, leanZ: -0.06, crownBreak: 0.26 },
  { profile: [0.65, 0.79, 0.71, 0.87, 0.66, 0.77, 0.6, 0.7, 0.53, 0.63, 0.47, 0.56, 0.41, 0.34, 0.27], phase: 4.3, leanX: 0.2, leanZ: -0.12, crownBreak: 0.38 },
  { profile: [0.8, 0.94, 0.86, 0.97, 0.78, 0.88, 0.69, 0.77, 0.61, 0.69, 0.54, 0.6, 0.46, 0.4, 0.34], phase: 5.2, leanX: -0.11, leanZ: 0.18, crownBreak: 0.3 },
] as const;

const createKarstRockGeometry = (archetypeIndex: number): THREE.BufferGeometry => {
  const radialSegments = 28;
  const rings = 28;
  const positions: number[] = [];
  const uvs: number[] = [];
  const colors: number[] = [];
  const indices: number[] = [];
  const archetype = KARST_ARCHETYPES[archetypeIndex % KARST_ARCHETYPES.length] ?? KARST_ARCHETYPES[0];

  for (let ring = 0; ring <= rings; ring += 1) {
    const vertical = ring / rings;
    const profilePosition = vertical * (archetype.profile.length - 1);
    const lowerProfileIndex = Math.floor(profilePosition);
    const upperProfileIndex = Math.min(archetype.profile.length - 1, lowerProfileIndex + 1);
    const lowerRadius = archetype.profile[lowerProfileIndex] ?? 0.2;
    const upperRadius = archetype.profile[upperProfileIndex] ?? lowerRadius;
    const profileBlend = profilePosition - lowerProfileIndex;
    const weathering =
      1 +
      Math.sin(vertical * 31 + archetype.phase * 2.1) * 0.032 +
      Math.sin(vertical * 67 - archetype.phase) * 0.016;
    const ringRadius = THREE.MathUtils.lerp(lowerRadius, upperRadius, profileBlend) * weathering;
    const centerX = Math.sin(vertical * 3.1 + archetype.phase) * archetype.leanX * vertical;
    const centerZ = Math.cos(vertical * 2.7 - archetype.phase) * archetype.leanZ * vertical;
    const stratum = THREE.MathUtils.clamp(
      0.89 +
        Math.sin(vertical * 104 + archetype.phase * 2.4) * 0.055 +
        Math.sin(vertical * 37 - archetype.phase) * 0.025,
      0.79,
      1,
    );
    for (let segment = 0; segment <= radialSegments; segment += 1) {
      const angle = segment / radialSegments * Math.PI * 2;
      const crownBreak =
        vertical > 0.7
          ? Math.max(0, Math.cos(angle - archetype.phase * 1.37)) *
            archetype.crownBreak *
            ((vertical - 0.7) / 0.3)
          : 0;
      const erosion =
        1 +
        Math.sin(angle * 3 + archetype.phase) * 0.13 +
        Math.sin(angle * 7 - archetype.phase * 1.7) * 0.065 +
        Math.sin(vertical * 24 + archetype.phase * 2.2) * 0.045 -
        crownBreak;
      positions.push(
        centerX + Math.cos(angle) * ringRadius * Math.max(0.42, erosion),
        vertical - 0.5 + Math.sin(angle * 2 + archetype.phase) * 0.012 * (0.3 + vertical),
        centerZ + Math.sin(angle) * ringRadius * Math.max(0.42, erosion),
      );
      uvs.push(segment / radialSegments, vertical * 4.4);
      colors.push(stratum, stratum * 0.975, stratum * 0.91);
    }
  }

  const verticesPerRing = radialSegments + 1;
  for (let ring = 0; ring < rings; ring += 1) {
    for (let segment = 0; segment < radialSegments; segment += 1) {
      const current = ring * verticesPerRing + segment;
      const next = current + verticesPerRing;
      indices.push(current, next, current + 1, next, next + 1, current + 1);
    }
  }

  const geometry = new THREE.BufferGeometry();
  const topCenter = positions.length / 3;
  positions.push(archetype.leanX * 0.7, 0.505, archetype.leanZ * 0.7);
  uvs.push(0.5, 1);
  colors.push(0.9, 0.87, 0.79);
  const topRingStart = rings * verticesPerRing;
  for (let segment = 0; segment < radialSegments; segment += 1) {
    indices.push(topRingStart + segment, topCenter, topRingStart + segment + 1);
  }
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
  geometry.setAttribute("color", new THREE.Float32BufferAttribute(colors, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  geometry.computeBoundingSphere();
  return geometry;
};

const createStripGeometry = (
  track: SuntraceTrack,
  innerOffset: number,
  outerOffset: number,
  height: number,
  segments: number,
  startProgress = 0,
  endProgress = 1,
): THREE.BufferGeometry => {
  const positions = new Float32Array((segments + 1) * 6);
  const uvs = new Float32Array((segments + 1) * 4);
  const indices: number[] = [];
  const frame = track.sample(0);

  for (let index = 0; index <= segments; index += 1) {
    const factor = index / segments;
    const progress = THREE.MathUtils.lerp(startProgress, endProgress, factor);
    track.sample(progress, 0, frame);
    const inner = frame.center
      .clone()
      .addScaledVector(frame.right, innerOffset)
      .addScaledVector(frame.normal, height);
    const outer = frame.center
      .clone()
      .addScaledVector(frame.right, outerOffset)
      .addScaledVector(frame.normal, height);
    positions.set([inner.x, inner.y, inner.z, outer.x, outer.y, outer.z], index * 6);
    uvs.set([0, factor, 1, factor], index * 4);

    if (index < segments) {
      const vertex = index * 2;
      indices.push(vertex, vertex + 2, vertex + 1, vertex + 2, vertex + 3, vertex + 1);
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute("uv", new THREE.BufferAttribute(uvs, 2));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  geometry.computeBoundingSphere();
  return geometry;
};

const createOffsetWallGeometry = (
  track: SuntraceTrack,
  offset: number,
  bottomHeight: number,
  topHeight: number,
  segments: number,
  startProgress = 0,
  endProgress = 1,
): THREE.BufferGeometry => {
  const positions = new Float32Array((segments + 1) * 6);
  const uvs = new Float32Array((segments + 1) * 4);
  const indices: number[] = [];
  const frame = track.sample(0);

  for (let index = 0; index <= segments; index += 1) {
    const progress = THREE.MathUtils.lerp(
      startProgress,
      endProgress,
      index / segments,
    );
    track.sample(progress, offset, frame);
    const bottom = frame.position.clone().addScaledVector(frame.normal, bottomHeight);
    const top = frame.position.clone().addScaledVector(frame.normal, topHeight);
    positions.set([bottom.x, bottom.y, bottom.z, top.x, top.y, top.z], index * 6);
    uvs.set([progress * 96, 0, progress * 96, 1], index * 4);
    if (index < segments) {
      const vertex = index * 2;
      indices.push(vertex, vertex + 2, vertex + 1, vertex + 2, vertex + 3, vertex + 1);
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute("uv", new THREE.BufferAttribute(uvs, 2));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  geometry.computeBoundingSphere();
  return geometry;
};

export const CAPE_ASTER_SET_PIECE_ENVELOPE = Object.freeze({
  startProgress: 0.132,
  endProgress: 0.178,
  innerLateral: -11.21,
  outerLateral: -24.5,
  reservedAssetInnerLateral: -25.4,
  reservedAssetOuterLateral: -37,
  minimumRoadClearance: 4.5,
});

export const CAPE_ASTER_GATEWAY_ENVELOPE = Object.freeze({
  progress: 0.164,
  supportLateral: 12,
  supportWidth: 0.42,
  deckBottomHeight: 6.15,
  roofTopHeight: 9.3,
  alongTrackDepth: 3.8,
});

export const SUNTRACE_START_GANTRY_IDENTITY_CONTRACT = Object.freeze({
  panelWidth: 8.6,
  panelHeight: 1.5,
  panelDepth: 0.18,
  panelCenterHeight: 7.2,
  accentCount: 0,
  mountCount: 2,
  mountHeight: 0.3,
});

const CAPE_TERRACE_START = 0.072;
const CAPE_TERRACE_END = 0.192;
const CAPE_TERRACE_INNER_OFFSET = -13.5;
const CAPE_TERRACE_OUTER_OFFSET = -36;
const CAPE_ASTER_SIGN_PROGRESS = 0.126;
const CAPE_ASTER_SIGN_LATERAL = -12.5;
const CAPE_ASTER_GATEWAY_SUPPORT_TOP_HEIGHT = 9.1;

export const CAPE_ASTER_SUPPORT_PILLAR_CONTRACT = Object.freeze({
  topRadius: 0.52,
  bottomRadius: 0.56,
  radialSegments: 8,
  rotation: Math.PI / 8,
});

export const CAPE_ASTER_SUPPORT_BRACING_CONTRACT = Object.freeze({
  radialSegments: 6,
  thickness: 0.12,
  bracesPerSupport: 2,
  longitudinalBays: 6,
  longitudinalBracesPerBay: 4,
  underdeckBays: 10,
  underdeckBracesPerBay: 4,
});

export const CAPE_ASTER_SUPPORT_MATERIAL_RESPONSE = Object.freeze({
  concreteColor: 0x858c89,
  concreteEmissive: 0x1f2524,
  concreteEmissiveIntensity: 0.1,
  concreteRoughness: 0.92,
  concreteEnvMapIntensity: 0.22,
});

export const CAPE_ASTER_SUPPORT_STEEL_RESPONSE = Object.freeze({
  color: 0x7f8886,
  emissive: 0x2d3738,
  emissiveIntensity: 0.24,
  roughness: 0.55,
  metalness: 0.52,
  envMapIntensity: 0.9,
});

export const CAPE_ASTER_BRIDGE_FASCIA_RESPONSE = Object.freeze({
  color: 0x5b625f,
  emissive: 0x151b1b,
  emissiveIntensity: 0.08,
  roughness: 0.88,
  envMapIntensity: 0.34,
  metalness: 0.18,
});

export const CAPE_ASTER_START_CENTER_MARKING_CONTRACT = Object.freeze({
  progressOffset: 0.018,
  clearanceProgress: 0.055,
});
export const CAPE_ASTER_ROUTE_TRANSITION_ENVELOPE = Object.freeze({
  startProgress: CAPE_TERRACE_START,
  endProgress: CAPE_TERRACE_END,
  innerLateral: -12.1,
  outerLateral: -29.2,
  minimumRoadClearance: 4.5,
  maximumStoneHeightScale: 2.2,
});

export const CAPE_ASTER_TERRACE_SURFACE_CONTRACT = Object.freeze({
  retainingWallVisible: false,
  terraceWallVisible: false,
  copingVisible: true,
});

export const CAPE_ASTER_ROUTE_STONE_PLACEMENTS = Object.freeze([
  { progress: 0.074, lateral: -13.4, scale: 0.82, heightScale: 1.55 },
  { progress: 0.079, lateral: -17.1, scale: 1.18, heightScale: 1.28 },
  { progress: 0.085, lateral: -14.6, scale: 0.96, heightScale: 1.92 },
  { progress: 0.091, lateral: -20.2, scale: 0.78, heightScale: 1.34 },
  { progress: 0.097, lateral: -13.7, scale: 1.24, heightScale: 1.48 },
  { progress: 0.103, lateral: -17.8, scale: 0.88, heightScale: 2.08 },
  { progress: 0.109, lateral: -14.9, scale: 1.06, heightScale: 1.36 },
  { progress: 0.115, lateral: -21.6, scale: 0.74, heightScale: 1.82 },
  { progress: 0.121, lateral: -13.8, scale: 1.28, heightScale: 1.42 },
  { progress: 0.127, lateral: -18.7, scale: 0.9, heightScale: 2.2 },
  { progress: 0.133, lateral: -15.3, scale: 1.08, heightScale: 1.5 },
  { progress: 0.139, lateral: -22.4, scale: 0.82, heightScale: 1.72 },
  { progress: 0.145, lateral: -13.9, scale: 1.2, heightScale: 1.38 },
  { progress: 0.151, lateral: -19.5, scale: 0.76, heightScale: 2.04 },
  { progress: 0.157, lateral: -15.5, scale: 1.3, heightScale: 1.32 },
  { progress: 0.163, lateral: -23.1, scale: 0.86, heightScale: 1.78 },
  { progress: 0.169, lateral: -14.4, scale: 0.92, heightScale: 1.94 },
  { progress: 0.175, lateral: -18.2, scale: 1.16, heightScale: 1.4 },
  { progress: 0.181, lateral: -15.8, scale: 1.02, heightScale: 2.12 },
  { progress: 0.187, lateral: -21.2, scale: 0.8, heightScale: 1.52 },
  { progress: 0.191, lateral: -13.6, scale: 1.12, heightScale: 1.68 },
] as const);

const getCapeTerraceSurfaceHeight = (
  frame: TrackFrame,
  lateralOffset: number,
): number => {
  const position = frame.center
    .clone()
    .addScaledVector(frame.right, lateralOffset);
  const naturalHeight = getIslandSurfaceHeight(position.x, position.z) + 0.16;
  const distanceFromRoad = Math.abs(lateralOffset);
  const innerTerrace = THREE.MathUtils.smoothstep(distanceFromRoad, 15.5, 24.5);
  const outerTerrace = THREE.MathUtils.smoothstep(distanceFromRoad, 24.5, 36);
  const upperLevel = THREE.MathUtils.lerp(1.12, 0.42, innerTerrace);
  const sculptedDrop = THREE.MathUtils.lerp(upperLevel, -5.1, outerTerrace);
  return Math.max(naturalHeight, frame.center.y + sculptedDrop);
};

export type UprightTrackPlacement = {
  position: THREE.Vector3;
  quaternion: THREE.Quaternion;
};

export const ASTER_HALO_ENVELOPE = Object.freeze({
  progress: 0.156,
  lateral: -23.5,
  halfWidth: 11.1,
  halfDepth: 20.5,
  topHeight: 28.2,
  minimumRoadClearance: 4.5,
});

export const ASTER_HALO_ROADSIDE_FORMS = Object.freeze([
  {
    progress: 0.102,
    lateral: 12.4,
    halfWidth: 0.62,
    height: 4.8,
    kind: "blade",
  },
  {
    progress: 0.136,
    lateral: -12.3,
    halfWidth: 0.7,
    height: 5.9,
    kind: "aperture",
  },
  {
    progress: 0.188,
    lateral: 12.6,
    halfWidth: 0.58,
    height: 7.2,
    kind: "sail",
  },
  {
    progress: 0.224,
    lateral: -12.5,
    halfWidth: 0.66,
    height: 4.15,
    kind: "stela",
  },
] as const);

export type CapeAsterCoastalSetPieceElement = {
  progress: number;
  lateral: number;
  width: number;
  height: number;
  depth: number;
  yaw: number;
  lean: number;
};

export type CapeAsterCoastalSetPieceZone = {
  id: "tide-calendars" | "breakwater-wings";
  name: string;
  silhouette: "vertical-rhythm" | "folded-horizon";
  startProgress: number;
  endProgress: number;
  minimumRoadClearance: number;
  elements: readonly CapeAsterCoastalSetPieceElement[];
};

export const CAPE_ASTER_COASTAL_SET_PIECE_ZONES = Object.freeze([
  {
    id: "tide-calendars",
    name: "Cape Aster tide-gauge beacons",
    silhouette: "vertical-rhythm",
    startProgress: 0.116,
    endProgress: 0.139,
    minimumRoadClearance: 4.5,
    elements: [
      { progress: 0.116, lateral: -24.8, width: 1.35, height: 7.4, depth: 1.15, yaw: 0.12, lean: -0.05 },
      { progress: 0.123, lateral: -26.2, width: 1.55, height: 9.1, depth: 1.25, yaw: -0.16, lean: 0.04 },
      { progress: 0.131, lateral: -24.6, width: 1.4, height: 8.2, depth: 1.05, yaw: 0.22, lean: -0.035 },
      { progress: 0.139, lateral: -27.1, width: 1.65, height: 9.7, depth: 1.3, yaw: -0.1, lean: 0.055 },
    ],
  },
  {
    id: "breakwater-wings",
    name: "Cape Aster breakwater telemetry frames",
    silhouette: "folded-horizon",
    startProgress: 0.144,
    endProgress: 0.16,
    minimumRoadClearance: 4.5,
    elements: [
      { progress: 0.144, lateral: 17.2, width: 4.5, height: 4.8, depth: 1.35, yaw: -0.08, lean: 0.08 },
      { progress: 0.152, lateral: 18.5, width: 5.1, height: 5.4, depth: 1.5, yaw: 0.1, lean: 0.11 },
      { progress: 0.16, lateral: 17.8, width: 4.7, height: 5, depth: 1.25, yaw: -0.14, lean: 0.07 },
    ],
  },
] as const satisfies readonly CapeAsterCoastalSetPieceZone[]);

export type CapeAsterCoastalSetPiecePlacement = UprightTrackPlacement & {
  foundation: THREE.Vector3;
  foundationHeight: number;
};

export const getCapeAsterCoastalSetPiecePlacement = (
  track: SuntraceTrack,
  element: CapeAsterCoastalSetPieceElement,
  localLateral = 0,
  localLongitudinal = 0,
): CapeAsterCoastalSetPiecePlacement => {
  const frame = track.samplePlacement(element.progress);
  const position = frame.center
    .clone()
    .addScaledVector(frame.right, element.lateral);
  const yaw = new THREE.Quaternion().setFromAxisAngle(
    new THREE.Vector3(0, 1, 0),
    element.yaw,
  );
  const quaternion = frame.quaternion.clone().multiply(yaw);
  const foundation = new THREE.Vector3(
    localLateral,
    0,
    localLongitudinal,
  )
    .applyQuaternion(quaternion)
    .add(position);
  foundation.y = Math.min(
    position.y - 0.5,
    getIslandSurfaceHeight(foundation.x, foundation.z) + 0.04,
  );
  return {
    position,
    quaternion,
    foundation,
    foundationHeight: position.y - foundation.y,
  };
};

export const getUprightRoadsidePlacement = (
  track: SuntraceTrack,
  progress: number,
  lateralOffset: number,
): UprightTrackPlacement => {
  const roadFrame = track.sample(progress);
  const placementFrame = track.samplePlacement(progress);
  return {
    position: roadFrame.center
      .clone()
      .addScaledVector(roadFrame.right, lateralOffset),
    quaternion: placementFrame.quaternion.clone(),
  };
};

export const getAsterHaloPlacement = (
  track: SuntraceTrack,
): UprightTrackPlacement => {
  const frame = track.samplePlacement(ASTER_HALO_ENVELOPE.progress);
  const position = frame.center
    .clone()
    .addScaledVector(frame.right, ASTER_HALO_ENVELOPE.lateral);
  position.y = getCapeTerraceSurfaceHeight(frame, ASTER_HALO_ENVELOPE.lateral) + 0.04;
  return {
    position,
    quaternion: frame.quaternion.clone(),
  };
};

export type AsterHaloMaterials = {
  limestone: THREE.Material;
  safety: THREE.Material;
  steel: THREE.Material;
};

export const createAsterHaloLandmark = (
  materials: AsterHaloMaterials,
  geometries: THREE.BufferGeometry[] = [],
): THREE.Group => {
  const group = new THREE.Group();
  group.name = "Aster Halo landmark";
  const ringGeometry = new THREE.TorusGeometry(10.2, 0.68, 10, 64);
  const accentRingGeometry = new THREE.TorusGeometry(7.85, 0.16, 6, 48);
  const hubGeometry = new THREE.CylinderGeometry(1.18, 1.42, 1.12, 12);
  hubGeometry.rotateX(Math.PI / 2);
  const petalShape = new THREE.Shape();
  petalShape.moveTo(-0.72, 1.45);
  petalShape.lineTo(-1.72, 5.8);
  petalShape.lineTo(0.12, 8.65);
  petalShape.lineTo(1.28, 5.38);
  petalShape.lineTo(0.68, 1.45);
  petalShape.closePath();
  const petalGeometry = new THREE.ExtrudeGeometry(petalShape, {
    depth: 0.42,
    bevelEnabled: true,
    bevelSegments: 1,
    bevelSize: 0.08,
    bevelThickness: 0.08,
    steps: 1,
  });
  petalGeometry.translate(0, 0, -0.21);
  const strutGeometry = new THREE.BoxGeometry(0.32, 7.2, 0.42);
  const supportGeometry = new THREE.BoxGeometry(1, 1, 1);
  geometries.push(
    ringGeometry,
    accentRingGeometry,
    hubGeometry,
    petalGeometry,
    strutGeometry,
    supportGeometry,
  );

  const haloCenter = new THREE.Vector3(0, 17.25, -12.4);
  const outerRing = new THREE.Mesh(ringGeometry, materials.steel);
  outerRing.name = "Aster Halo outer ring";
  outerRing.position.copy(haloCenter);
  outerRing.castShadow = true;
  outerRing.receiveShadow = true;
  const accentRing = new THREE.Mesh(accentRingGeometry, materials.safety);
  accentRing.name = "Aster Halo red inner orbit";
  accentRing.position.copy(haloCenter).add(new THREE.Vector3(0, 0, -0.08));
  const hub = new THREE.Mesh(hubGeometry, materials.steel);
  hub.position.copy(haloCenter);
  hub.castShadow = true;
  const rotor = new THREE.Group();
  rotor.name = "Aster Halo kinetic petals";
  rotor.position.copy(haloCenter).add(new THREE.Vector3(0, 0, 0.04));
  const petals = new THREE.InstancedMesh(petalGeometry, materials.limestone, 5);
  const struts = new THREE.InstancedMesh(strutGeometry, materials.steel, 5);
  const dummy = new THREE.Object3D();
  for (let index = 0; index < 5; index += 1) {
    const angle = index * Math.PI * 2 / 5 + 0.16;
    dummy.position.set(0, 0, 0);
    dummy.rotation.set(0, 0, angle);
    dummy.scale.set(1, 1, 1);
    dummy.updateMatrix();
    petals.setMatrixAt(index, dummy.matrix);
    dummy.position.set(-Math.sin(angle) * 4.15, Math.cos(angle) * 4.15, -0.08);
    dummy.updateMatrix();
    struts.setMatrixAt(index, dummy.matrix);
  }
  for (const mesh of [petals, struts]) {
    mesh.instanceMatrix.needsUpdate = true;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.computeBoundingSphere();
  }
  rotor.add(petals, struts);

  const addSupport = (
    start: readonly [number, number],
    end: readonly [number, number],
  ): void => {
    const deltaX = end[0] - start[0];
    const deltaY = end[1] - start[1];
    const support = new THREE.Mesh(supportGeometry, materials.limestone);
    support.position.set(
      (start[0] + end[0]) * 0.5,
      (start[1] + end[1]) * 0.5,
      haloCenter.z,
    );
    support.rotation.z = -Math.atan2(deltaX, deltaY);
    support.scale.set(1.35, Math.hypot(deltaX, deltaY), 1.65);
    support.castShadow = true;
    support.receiveShadow = true;
    group.add(support);
  };
  addSupport([-7.1, 0.85], [-3.45, 8.55]);
  addSupport([7.1, 0.85], [3.45, 8.55]);
  for (const lateral of [-7.1, 7.1]) {
    const plinth = new THREE.Mesh(supportGeometry, materials.limestone);
    plinth.position.set(lateral, 0.42, haloCenter.z);
    plinth.scale.set(3.1, 0.84, 3.15);
    plinth.castShadow = true;
    plinth.receiveShadow = true;
    group.add(plinth);
  }
  const cradle = new THREE.Mesh(supportGeometry, materials.steel);
  cradle.position.set(0, 8.35, haloCenter.z);
  cradle.scale.set(8.4, 0.72, 1.45);
  cradle.castShadow = true;
  cradle.receiveShadow = true;
  group.add(cradle, outerRing, accentRing, hub, rotor);
  return group;
};

export type CapeAsterCoastalSetPieceMaterials = {
  limestone: THREE.Material;
  limestoneDark: THREE.Material;
  safety: THREE.Material;
  steel: THREE.Material;
};

type CapeAsterCoastalMatrixSet = {
  foundations: THREE.Matrix4[];
  limestone: THREE.Matrix4[];
  safety: THREE.Matrix4[];
  steel: THREE.Matrix4[];
};

export const createCapeAsterCoastalSetPieces = (
  track: SuntraceTrack,
  materials: CapeAsterCoastalSetPieceMaterials,
  geometries: THREE.BufferGeometry[] = [],
): THREE.Group => {
  const group = new THREE.Group();
  group.name = "Cape Aster coastal route story";
  const foundationGeometry = new THREE.CylinderGeometry(0.38, 0.5, 1, 8, 1);
  const limestoneGeometry = new THREE.CylinderGeometry(0.34, 0.5, 1, 6, 1);
  const steelGeometry = new THREE.CylinderGeometry(0.44, 0.5, 1, 8, 1);
  const identityShape = new THREE.Shape();
  identityShape.moveTo(-0.38, -0.5);
  identityShape.lineTo(0.5, -0.5);
  identityShape.lineTo(0.5, 0.24);
  identityShape.lineTo(0.26, 0.5);
  identityShape.lineTo(-0.5, 0.5);
  identityShape.lineTo(-0.5, -0.28);
  identityShape.closePath();
  const identityGeometry = new THREE.ExtrudeGeometry(identityShape, {
    depth: 0.16,
    bevelEnabled: true,
    bevelSegments: 1,
    bevelSize: 0.04,
    bevelThickness: 0.04,
    steps: 1,
  });
  identityGeometry.center();
  identityGeometry.computeBoundingBox();
  const identitySize = identityGeometry.boundingBox?.getSize(
    new THREE.Vector3(),
  ) ?? new THREE.Vector3(1, 1, 1);
  identityGeometry.scale(
    1 / identitySize.x,
    1 / identitySize.y,
    1 / identitySize.z,
  );
  geometries.push(
    foundationGeometry,
    limestoneGeometry,
    steelGeometry,
    identityGeometry,
  );
  const root = new THREE.Object3D();
  const primitive = new THREE.Object3D();
  const localUp = new THREE.Vector3(0, 1, 0);
  const segmentStart = new THREE.Vector3();
  const segmentEnd = new THREE.Vector3();
  const segmentDelta = new THREE.Vector3();
  const segmentDirection = new THREE.Vector3();
  const appendPrimitive = (
    target: THREE.Matrix4[],
    position: readonly [number, number, number],
    scale: readonly [number, number, number],
    rotation: readonly [number, number, number] = [0, 0, 0],
  ): void => {
    primitive.position.set(...position);
    primitive.rotation.set(...rotation);
    primitive.scale.set(...scale);
    primitive.updateMatrix();
    target.push(
      new THREE.Matrix4().multiplyMatrices(root.matrix, primitive.matrix),
    );
  };
  const appendSegment = (
    target: THREE.Matrix4[],
    start: readonly [number, number, number],
    end: readonly [number, number, number],
    width: number,
    depth: number,
  ): void => {
    segmentStart.set(...start);
    segmentEnd.set(...end);
    segmentDelta.subVectors(segmentEnd, segmentStart);
    primitive.position.copy(segmentStart).add(segmentEnd).multiplyScalar(0.5);
    primitive.quaternion.setFromUnitVectors(
      localUp,
      segmentDirection.copy(segmentDelta).normalize(),
    );
    primitive.scale.set(width, segmentDelta.length(), depth);
    primitive.updateMatrix();
    target.push(
      new THREE.Matrix4().multiplyMatrices(root.matrix, primitive.matrix),
    );
  };
  const appendFoundation = (
    target: THREE.Matrix4[],
    element: CapeAsterCoastalSetPieceElement,
    localLateral: number,
    localLongitudinal: number,
    width: number,
    depth: number,
  ): void => {
    const placement = getCapeAsterCoastalSetPiecePlacement(
      track,
      element,
      localLateral,
      localLongitudinal,
    );
    appendPrimitive(
      target,
      [
        localLateral,
        placement.foundationHeight * -0.5,
        localLongitudinal,
      ],
      [width, placement.foundationHeight, depth],
    );
  };
  const createInstancedMesh = (
    matrices: readonly THREE.Matrix4[],
    geometry: THREE.BufferGeometry,
    material: THREE.Material,
    name: string,
    castShadow: boolean,
  ): THREE.InstancedMesh => {
    const mesh = new THREE.InstancedMesh(
      geometry,
      material,
      matrices.length,
    );
    mesh.name = name;
    matrices.forEach((matrix, index) => mesh.setMatrixAt(index, matrix));
    mesh.instanceMatrix.setUsage(THREE.StaticDrawUsage);
    mesh.instanceMatrix.needsUpdate = true;
    mesh.castShadow = castShadow;
    mesh.receiveShadow = true;
    mesh.computeBoundingBox();
    mesh.computeBoundingSphere();
    return mesh;
  };

  for (const zone of CAPE_ASTER_COASTAL_SET_PIECE_ZONES) {
    const zoneGroup = new THREE.Group();
    zoneGroup.name = zone.name;
    const matrices: CapeAsterCoastalMatrixSet = {
      foundations: [],
      limestone: [],
      safety: [],
      steel: [],
    };

    for (const element of zone.elements) {
      const placement = getCapeAsterCoastalSetPiecePlacement(track, element);
      root.position.copy(placement.position);
      root.quaternion.copy(placement.quaternion);
      root.scale.set(1, 1, 1);
      root.updateMatrix();

      if (zone.id === "tide-calendars") {
        appendFoundation(
          matrices.foundations,
          element,
          0,
          0,
          element.width * 1.12,
          element.depth * 1.1,
        );
        appendPrimitive(
          matrices.limestone,
          [0, 0.28, 0],
          [element.width * 1.2, 0.56, element.depth * 1.15],
        );
        const mastBase = 0.52;
        const mastTopX = Math.tan(element.lean) * (element.height - mastBase);
        appendSegment(
          matrices.steel,
          [0, mastBase, 0],
          [mastTopX, element.height, 0],
          0.28,
          0.32,
        );
        appendSegment(
          matrices.steel,
          [mastTopX - element.width * 0.54, element.height - 0.5, 0],
          [mastTopX + element.width * 0.54, element.height - 0.12, 0],
          0.18,
          0.22,
        );
        for (const [index, ratio] of [0.34, 0.52, 0.7].entries()) {
          const height = THREE.MathUtils.lerp(mastBase, element.height, ratio);
          const lateral = mastTopX * ratio;
          appendPrimitive(
            matrices.safety,
            [lateral + element.width * 0.14, height, element.depth * 0.5],
            [element.width * (0.34 + index * 0.04), 0.14, 0.12],
          );
        }
      } else {
        for (const side of [-1, 1]) {
          const foundationLateral = side * element.width * 0.28;
          appendFoundation(
            matrices.foundations,
            element,
            foundationLateral,
            0,
            0.62,
            element.depth * 0.72,
          );
        }
        const crownHeight = element.height * 0.76;
        const crownShift = Math.tan(element.lean) * element.height * 0.24;
        const leftCrown = crownShift - element.width * 0.1;
        const rightCrown = crownShift + element.width * 0.1;
        appendSegment(
          matrices.steel,
          [element.width * -0.28, 0.24, 0],
          [leftCrown, crownHeight, 0],
          0.28,
          0.32,
        );
        appendSegment(
          matrices.steel,
          [element.width * 0.28, 0.24, 0],
          [rightCrown, crownHeight, 0],
          0.28,
          0.32,
        );
        appendSegment(
          matrices.steel,
          [leftCrown, crownHeight, 0],
          [rightCrown, crownHeight, 0],
          0.22,
          0.26,
        );
        appendPrimitive(
          matrices.limestone,
          [crownShift, crownHeight + 0.34, 0],
          [element.width * 0.22, 0.62, element.depth * 0.72],
        );
        appendPrimitive(
          matrices.safety,
          [crownShift, crownHeight + 0.36, element.depth * 0.42],
          [element.width * 0.16, 0.2, 0.1],
        );
      }
    }

    zoneGroup.add(
      createInstancedMesh(
        matrices.foundations,
        foundationGeometry,
        materials.limestoneDark,
        `${zone.name} foundations`,
        true,
      ),
      createInstancedMesh(
        matrices.limestone,
        limestoneGeometry,
        materials.limestone,
        `${zone.name} stone instrument pods`,
        true,
      ),
      createInstancedMesh(
        matrices.steel,
        steelGeometry,
        materials.steel,
        `${zone.name} tapered steelwork`,
        true,
      ),
      createInstancedMesh(
        matrices.safety,
        identityGeometry,
        materials.safety,
        `${zone.name} vermilion gauge marks`,
        false,
      ),
    );
    group.add(zoneGroup);
  }

  return group;
};

export const getCapeAsterSignPlacement = (
  track: SuntraceTrack,
): UprightTrackPlacement => {
  const frame = track.samplePlacement(CAPE_ASTER_SIGN_PROGRESS);
  const position = frame.center
    .clone()
    .addScaledVector(frame.right, CAPE_ASTER_SIGN_LATERAL);
  position.y = getCapeTerraceSurfaceHeight(frame, CAPE_ASTER_SIGN_LATERAL);
  return {
    position,
    quaternion: frame.quaternion.clone(),
  };
};

export type CapeAsterGatewaySupportPlacement = UprightTrackPlacement & {
  foundation: THREE.Vector3;
  height: number;
};

export const getCapeAsterGatewaySupportPlacement = (
  track: SuntraceTrack,
  lateralOffset: number,
): CapeAsterGatewaySupportPlacement => {
  const roadFrame = track.sample(CAPE_ASTER_GATEWAY_ENVELOPE.progress);
  const placementFrame = track.samplePlacement(
    CAPE_ASTER_GATEWAY_ENVELOPE.progress,
  );
  const foundation = roadFrame.center
    .clone()
    .addScaledVector(roadFrame.right, lateralOffset);
  const top = placementFrame.center.y + CAPE_ASTER_GATEWAY_SUPPORT_TOP_HEIGHT;
  const height = top - foundation.y;
  const position = foundation.clone();
  position.y += height * 0.5;
  return {
    foundation,
    height,
    position,
    quaternion: placementFrame.quaternion.clone(),
  };
};

export const createCapeAsterBridgeGirderGeometry = (
  track: SuntraceTrack,
): THREE.BufferGeometry => {
  const startProgress = 0.064;
  const endProgress = 0.151;
  const segments = 52;
  const girderOffsets = [-4.85, 0, 4.85] as const;
  const halfWidth = 0.24;
  const bottomHeight = -2.08;
  const topHeight = -0.85;
  const positions: number[] = [];
  const routeProgress: number[] = [];
  const lateralOffset: number[] = [];
  const heightOffset: number[] = [];
  const indices: number[] = [];
  const frame = track.sample(0);

  for (const girderOffset of girderOffsets) {
    const girderStart = positions.length / 3;
    for (let index = 0; index <= segments; index += 1) {
      const progress = THREE.MathUtils.lerp(
        startProgress,
        endProgress,
        index / segments,
      );
      track.sample(progress, 0, frame);
      for (const height of [bottomHeight, topHeight]) {
        for (const side of [-1, 1]) {
          const lateral = girderOffset + side * halfWidth;
          const position = frame.center
            .clone()
            .addScaledVector(frame.right, lateral)
            .addScaledVector(frame.normal, height);
          positions.push(position.x, position.y, position.z);
          routeProgress.push(progress);
          lateralOffset.push(lateral);
          heightOffset.push(height);
        }
      }
    }

    for (let index = 0; index < segments; index += 1) {
      const current = girderStart + index * 4;
      const next = current + 4;
      indices.push(
        current,
        current + 1,
        next,
        current + 1,
        next + 1,
        next,
        current + 2,
        next + 2,
        current + 3,
        current + 3,
        next + 2,
        next + 3,
        current,
        next,
        current + 2,
        current + 2,
        next,
        next + 2,
        current + 1,
        current + 3,
        next + 1,
        current + 3,
        next + 3,
        next + 1,
      );
    }

    const first = girderStart;
    const last = girderStart + segments * 4;
    indices.push(
      first,
      first + 2,
      first + 1,
      first + 1,
      first + 2,
      first + 3,
      last,
      last + 1,
      last + 2,
      last + 1,
      last + 3,
      last + 2,
    );
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute(
    "position",
    new THREE.Float32BufferAttribute(positions, 3),
  );
  geometry.setAttribute(
    "routeProgress",
    new THREE.Float32BufferAttribute(routeProgress, 1),
  );
  geometry.setAttribute(
    "lateralOffset",
    new THREE.Float32BufferAttribute(lateralOffset, 1),
  );
  geometry.setAttribute(
    "heightOffset",
    new THREE.Float32BufferAttribute(heightOffset, 1),
  );
  geometry.setIndex(indices);
  const hardEdgeGeometry = geometry.toNonIndexed();
  geometry.dispose();
  hardEdgeGeometry.computeVertexNormals();
  hardEdgeGeometry.computeBoundingBox();
  hardEdgeGeometry.computeBoundingSphere();
  return hardEdgeGeometry;
};

export const createCapeAsterGatewayGeometry = (
  track: SuntraceTrack,
): THREE.BufferGeometry => {
  const positions: number[] = [];
  const clearanceClass: number[] = [];
  const lateralOffset: number[] = [];
  const heightOffset: number[] = [];
  const indices: number[] = [];
  const frame = track.samplePlacement(CAPE_ASTER_GATEWAY_ENVELOPE.progress);
  const appendBox = (
    center: readonly [number, number, number],
    size: readonly [number, number, number],
    rotationZ: number,
    componentClass: 1 | 2,
    worldCenter?: THREE.Vector3,
  ): void => {
    const vertexStart = positions.length / 3;
    const cosine = Math.cos(rotationZ);
    const sine = Math.sin(rotationZ);
    const boxCenter = worldCenter ??
      frame.center
        .clone()
        .addScaledVector(frame.right, center[0])
        .addScaledVector(frame.normal, center[1])
        .addScaledVector(frame.tangent, center[2]);
    for (const zSide of [-1, 1]) {
      for (const ySide of [-1, 1]) {
        for (const xSide of [-1, 1]) {
          const localX = xSide * size[0] * 0.5;
          const localY = ySide * size[1] * 0.5;
          const rotatedX = localX * cosine - localY * sine;
          const rotatedY = localX * sine + localY * cosine;
          const lateral = center[0] + rotatedX;
          const position = boxCenter
            .clone()
            .addScaledVector(frame.right, rotatedX)
            .addScaledVector(frame.normal, rotatedY)
            .addScaledVector(frame.tangent, zSide * size[2] * 0.5);
          positions.push(position.x, position.y, position.z);
          clearanceClass.push(componentClass);
          lateralOffset.push(lateral);
          heightOffset.push(position.y - frame.center.y);
        }
      }
    }
    const faces = [
      [0, 2, 1, 1, 2, 3],
      [4, 5, 6, 5, 7, 6],
      [0, 1, 4, 1, 5, 4],
      [2, 6, 3, 3, 6, 7],
      [0, 4, 2, 2, 4, 6],
      [1, 3, 5, 3, 7, 5],
    ] as const;
    for (const face of faces) {
      indices.push(...face.map((index) => vertexStart + index));
    }
  };

  for (const side of [-1, 1]) {
    const lateral = side * CAPE_ASTER_GATEWAY_ENVELOPE.supportLateral;
    const support = getCapeAsterGatewaySupportPlacement(track, lateral);
    appendBox(
      [lateral, 0, 0],
      [CAPE_ASTER_GATEWAY_ENVELOPE.supportWidth, support.height, 0.54],
      0,
      1,
      support.position,
    );
    const footingCenter = support.foundation.clone();
    footingCenter.y += 0.2;
    appendBox(
      [lateral, 0, 0],
      [1.5, 0.4, 1.9],
      0,
      1,
      footingCenter,
    );
  }

  appendBox(
    [0, 6.32, 0],
    [23.42, 0.34, CAPE_ASTER_GATEWAY_ENVELOPE.alongTrackDepth],
    0,
    2,
  );
  appendBox(
    [0, 9.15, 0],
    [23.42, 0.3, 4.6],
    0,
    2,
  );
  appendBox([0, 7.74, 0], [0.24, 2.52, 0.34], 0, 2);

  const braceRun = 6.85;
  const braceRise = 2.36;
  const braceLength = Math.hypot(braceRun, braceRise);
  const braceAngle = Math.atan2(braceRise, braceRun);
  for (const side of [-1, 1]) {
    const centerX = side * 7.65;
    appendBox(
      [centerX, 7.74, -1.72],
      [braceLength, 0.22, 0.24],
      side * braceAngle,
      2,
    );
    appendBox(
      [centerX, 7.74, 1.72],
      [braceLength, 0.22, 0.24],
      side * -braceAngle,
      2,
    );
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute(
    "position",
    new THREE.Float32BufferAttribute(positions, 3),
  );
  geometry.setAttribute(
    "clearanceClass",
    new THREE.Float32BufferAttribute(clearanceClass, 1),
  );
  geometry.setAttribute(
    "lateralOffset",
    new THREE.Float32BufferAttribute(lateralOffset, 1),
  );
  geometry.setAttribute(
    "heightOffset",
    new THREE.Float32BufferAttribute(heightOffset, 1),
  );
  geometry.setIndex(indices);
  const hardEdgeGeometry = geometry.toNonIndexed();
  geometry.dispose();
  hardEdgeGeometry.computeVertexNormals();
  hardEdgeGeometry.computeBoundingBox();
  hardEdgeGeometry.computeBoundingSphere();
  return hardEdgeGeometry;
};

const createCapeTerraceGeometry = (
  track: SuntraceTrack,
  outerOffset: number,
  innerOffset: number,
  heightOffset = 0,
): THREE.BufferGeometry => {
  const progressSegments = 52;
  const lateralSegments = 10;
  const verticesPerRow = lateralSegments + 1;
  const positions: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];
  const frame = track.sample(0);

  for (let progressIndex = 0; progressIndex <= progressSegments; progressIndex += 1) {
    const progressFactor = progressIndex / progressSegments;
    const progress = THREE.MathUtils.lerp(
      CAPE_TERRACE_START,
      CAPE_TERRACE_END,
      progressFactor,
    );
    track.sample(progress, 0, frame);
    for (let lateralIndex = 0; lateralIndex <= lateralSegments; lateralIndex += 1) {
      const lateralFactor = lateralIndex / lateralSegments;
      const lateral = THREE.MathUtils.lerp(
        outerOffset,
        innerOffset,
        lateralFactor,
      );
      const position = frame.center
        .clone()
        .addScaledVector(frame.right, lateral);
      position.y = getCapeTerraceSurfaceHeight(frame, lateral) + heightOffset;
      positions.push(position.x, position.y, position.z);
      uvs.push(lateralFactor * 3.4, progressFactor * 8.2);
    }
  }

  for (let progressIndex = 0; progressIndex < progressSegments; progressIndex += 1) {
    for (let lateralIndex = 0; lateralIndex < lateralSegments; lateralIndex += 1) {
      const current = progressIndex * verticesPerRow + lateralIndex;
      const next = current + verticesPerRow;
      indices.push(current, next, current + 1, current + 1, next, next + 1);
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute(
    "position",
    new THREE.Float32BufferAttribute(positions, 3),
  );
  geometry.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  geometry.computeBoundingSphere();
  return geometry;
};

const createCapeTerraceRetainingWallGeometry = (
  track: SuntraceTrack,
): THREE.BufferGeometry => {
  const segments = 52;
  const positions: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];
  const frame = track.sample(0);

  for (let index = 0; index <= segments; index += 1) {
    const factor = index / segments;
    const progress = THREE.MathUtils.lerp(
      CAPE_TERRACE_START,
      CAPE_TERRACE_END,
      factor,
    );
    track.sample(progress, 0, frame);
    const position = frame.center
      .clone()
      .addScaledVector(frame.right, CAPE_TERRACE_OUTER_OFFSET);
    const naturalHeight = getIslandSurfaceHeight(position.x, position.z) - 0.24;
    const terraceHeight = getCapeTerraceSurfaceHeight(
      frame,
      CAPE_TERRACE_OUTER_OFFSET,
    );
    positions.push(
      position.x,
      naturalHeight,
      position.z,
      position.x,
      terraceHeight,
      position.z,
    );
    uvs.push(factor * 8, 0, factor * 8, 1);
    if (index < segments) {
      const vertex = index * 2;
      indices.push(
        vertex,
        vertex + 2,
        vertex + 1,
        vertex + 1,
        vertex + 2,
        vertex + 3,
      );
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute(
    "position",
    new THREE.Float32BufferAttribute(positions, 3),
  );
  geometry.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  geometry.computeBoundingSphere();
  return geometry;
};

export const SUNTRACE_LATE_OCEAN_ISLAND_SECTOR = Object.freeze({
  angleMin: -2.95,
  protectedAngleMin: -2.82,
  protectedAngleMax: -2.35,
  angleMax: -2.22,
  radialStart: 0.82,
  protectedRadialStart: 0.91,
  seaLevel: -3.1,
  submergedHeight: -4.8,
});

export const SUNTRACE_WORLD_PROP_GROUNDING_CONTRACT = Object.freeze({
  minimumVisibleSurfaceHeight:
    SUNTRACE_LATE_OCEAN_ISLAND_SECTOR.seaLevel + 0.85,
});

const getLateOceanIslandHeight = (
  angle: number,
  radialFactor: number,
  surfaceHeight: number,
): number => {
  const sector = SUNTRACE_LATE_OCEAN_ISLAND_SECTOR;
  if (
    angle <= sector.angleMin ||
    angle >= sector.angleMax ||
    radialFactor <= sector.radialStart
  ) {
    return surfaceHeight;
  }
  const angleInfluence = Math.min(
    THREE.MathUtils.smoothstep(
      angle,
      sector.angleMin,
      sector.protectedAngleMin,
    ),
    1 - THREE.MathUtils.smoothstep(
      angle,
      sector.protectedAngleMax,
      sector.angleMax,
    ),
  );
  const radialInfluence = THREE.MathUtils.smoothstep(
    radialFactor,
    sector.radialStart,
    sector.protectedRadialStart,
  );
  return THREE.MathUtils.lerp(
    surfaceHeight,
    sector.submergedHeight,
    angleInfluence * radialInfluence,
  );
};

export const getSuntraceRenderableSurfaceHeight = (
  x: number,
  z: number,
): number => {
  const normalizedX = x / 1.07;
  const normalizedZ = z / 0.91;
  const radialFactor = Math.hypot(normalizedX, normalizedZ) / 388;
  const angle = Math.atan2(normalizedZ, normalizedX);
  return getLateOceanIslandHeight(
    angle,
    radialFactor,
    getIslandSurfaceHeight(x, z),
  );
};

export const createIslandGeometry = (): THREE.BufferGeometry => {
  const radialSegments = 128;
  const rings = 18;
  const positions: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];

  for (let ring = 0; ring <= rings; ring += 1) {
    const radialFactor = ring / rings;
    for (let segment = 0; segment < radialSegments; segment += 1) {
      const angle = (segment / radialSegments) * Math.PI * 2;
      const edgeNoise =
        Math.sin(angle * 5.0) * 8 +
        Math.sin(angle * 11.0 + 0.8) * 4 +
        Math.sin(angle * 23.0 + 1.7) * 1.8;
      const radius = radialFactor * (388 + edgeNoise * radialFactor);
      const x = Math.cos(angle) * radius * 1.07;
      const z = Math.sin(angle) * radius * 0.91;
      const surfaceRadialFactor =
        Math.hypot(x / 1.07, z / 0.91) / 388;
      const y = getLateOceanIslandHeight(
        Math.atan2(Math.sin(angle), Math.cos(angle)),
        surfaceRadialFactor,
        getIslandSurfaceHeight(x, z),
      );
      positions.push(x, y, z);
      uvs.push(0.5 + x / 900, 0.5 + z / 900);
    }
  }

  for (let ring = 0; ring < rings; ring += 1) {
    for (let segment = 0; segment < radialSegments; segment += 1) {
      const next = (segment + 1) % radialSegments;
      const currentA = ring * radialSegments + segment;
      const currentB = ring * radialSegments + next;
      const nextA = (ring + 1) * radialSegments + segment;
      const nextB = (ring + 1) * radialSegments + next;
      indices.push(currentA, nextA, currentB, nextA, nextB, currentB);
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  geometry.computeBoundingSphere();
  return geometry;
};

const getShorelineRadius = (angle: number): number =>
  (388 +
    Math.sin(angle * 5) * 8 +
    Math.sin(angle * 11 + 0.8) * 4 +
    Math.sin(angle * 23 + 1.7) * 1.8) *
  0.968;

type CapeWaterlineLayer = "wet-rock" | "foam";

const getCapeWaterlineProfile = (
  factor: number,
  layer: CapeWaterlineLayer,
): { innerOffset: number; outerOffset: number } => {
  const shorelineWander =
    Math.sin(factor * Math.PI * 18 + 0.7) * 0.62 +
    Math.sin(factor * Math.PI * 43 + 2.1) * 0.24;
  const widthVariation =
    0.5 +
    Math.sin(factor * Math.PI * 26 + 1.6) * 0.24 +
    Math.sin(factor * Math.PI * 61 + 0.2) * 0.1;

  if (layer === "foam") {
    const center = 0.62 + shorelineWander * 0.72;
    const halfWidth = 0.28 + widthVariation * 0.42;
    return {
      innerOffset: center - halfWidth,
      outerOffset: center + halfWidth,
    };
  }

  const center = -0.12 + shorelineWander * 1.35;
  const halfWidth = 2.15 + widthVariation * 1.28;
  return {
    innerOffset: center - halfWidth,
    outerOffset: center + halfWidth,
  };
};

const isCapeWaterlineSegmentVisible = (
  factor: number,
  layer: CapeWaterlineLayer,
): boolean => {
  const breakup =
    Math.sin(factor * Math.PI * 14 + 0.45) +
    Math.sin(factor * Math.PI * 37 + 1.72) * 0.62;
  return layer === "foam" ? breakup > -0.08 : breakup > -1.12;
};

const createCapeWaterlineBandGeometry = (
  layer: CapeWaterlineLayer,
): THREE.BufferGeometry => {
  const segments = 96;
  const startAngle = 1.88;
  const endAngle = 2.68;
  const positions: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];

  for (let index = 0; index < segments; index += 1) {
    const startFactor = index / segments;
    const endFactor = (index + 1) / segments;
    const middleFactor = (startFactor + endFactor) * 0.5;
    if (!isCapeWaterlineSegmentVisible(middleFactor, layer)) {
      continue;
    }
    const vertex = positions.length / 3;
    for (const factor of [startFactor, endFactor]) {
      const angle = THREE.MathUtils.lerp(startAngle, endAngle, factor);
      const shorelineRadius = getShorelineRadius(angle);
      const profile = getCapeWaterlineProfile(factor, layer);
      for (const offset of [profile.innerOffset, profile.outerOffset]) {
        const radius = shorelineRadius + offset;
        positions.push(
          Math.cos(angle) * radius * 1.07,
          layer === "foam" ? -2.985 : -3.015,
          Math.sin(angle) * radius * 0.91,
        );
        uvs.push(factor * 12, offset === profile.innerOffset ? 0 : 1);
      }
    }
    indices.push(
      vertex,
      vertex + 2,
      vertex + 1,
      vertex + 1,
      vertex + 2,
      vertex + 3,
    );
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute(
    "position",
    new THREE.Float32BufferAttribute(positions, 3),
  );
  geometry.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  geometry.computeBoundingSphere();
  return geometry;
};

type CapeStoneArchetype = {
  width: number;
  height: number;
  depth: number;
  shear: number;
  lean: number;
  phase: number;
};

const CAPE_STONE_ARCHETYPES = [
  { width: 1.3, height: 0.68, depth: 0.88, shear: 0.18, lean: -0.12, phase: 0.3 },
  { width: 1.08, height: 0.82, depth: 1.34, shear: -0.14, lean: 0.18, phase: 1.7 },
  { width: 1.46, height: 0.6, depth: 1.03, shear: 0.09, lean: 0.08, phase: 3.2 },
  { width: 0.98, height: 0.76, depth: 1.48, shear: -0.2, lean: -0.16, phase: 4.8 },
] as const satisfies readonly CapeStoneArchetype[];

const createCapeStoneGeometry = (
  archetype: CapeStoneArchetype,
): THREE.BufferGeometry => {
  const geometry = new THREE.IcosahedronGeometry(1, 1);
  const positions = geometry.getAttribute("position");
  for (let index = 0; index < positions.count; index += 1) {
    const sourceX = positions.getX(index);
    const sourceY = positions.getY(index);
    const sourceZ = positions.getZ(index);
    const angle = Math.atan2(sourceZ, sourceX);
    const irregularity =
      1 +
      Math.sin(angle * 3 + archetype.phase) * 0.14 +
      Math.sin(angle * 7 - archetype.phase * 0.7) * 0.055 +
      Math.sin(sourceY * 8 + archetype.phase) * 0.045;
    const taper = 1 - Math.max(0, sourceY) * 0.11;
    positions.setXYZ(
      index,
      sourceX * archetype.width * irregularity * taper +
        sourceZ * archetype.shear +
        sourceY * archetype.lean,
      sourceY * archetype.height + Math.sin(angle * 2 + archetype.phase) * 0.055,
      sourceZ * archetype.depth * irregularity * taper - sourceX * archetype.shear * 0.45,
    );
  }
  positions.needsUpdate = true;
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
};

const getCapeScreeCenterLateral = (progress: number): number => {
  const factor = THREE.MathUtils.clamp(
    THREE.MathUtils.inverseLerp(
      CAPE_TERRACE_START,
      CAPE_TERRACE_END,
      progress,
    ),
    0,
    1,
  );
  return (
    THREE.MathUtils.lerp(-16.2, -24.4, factor) +
    Math.sin(factor * Math.PI * 5.4 + 0.35) * 1.05 +
    Math.sin(factor * Math.PI * 13.2 + 1.7) * 0.34
  );
};

export const createCapeScreeApronGeometry = (
  track: SuntraceTrack,
): THREE.BufferGeometry => {
  const progressSegments = 60;
  const lateralSegments = 5;
  const verticesPerRow = lateralSegments + 1;
  const positions: number[] = [];
  const colors: number[] = [];
  const indices: number[] = [];
  const frame = track.sample(0);
  const defaultStoneColor = new THREE.Color(0xd8b78a);
  const stoneColors = [
    defaultStoneColor,
    new THREE.Color(0xc8a274),
    new THREE.Color(0xe1c49b),
  ];

  for (let progressIndex = 0; progressIndex <= progressSegments; progressIndex += 1) {
    const progressFactor = progressIndex / progressSegments;
    const progress = THREE.MathUtils.lerp(
      CAPE_TERRACE_START,
      CAPE_TERRACE_END,
      progressFactor,
    );
    const centerLateral = getCapeScreeCenterLateral(progress);
    const width =
      7.2 +
      Math.sin(progressFactor * Math.PI * 7 + 0.6) * 0.82 +
      Math.sin(progressFactor * Math.PI * 17 + 1.3) * 0.34;
    track.sample(progress, 0, frame);
    for (let lateralIndex = 0; lateralIndex <= lateralSegments; lateralIndex += 1) {
      const lateralFactor = lateralIndex / lateralSegments;
      const lateral = centerLateral + (lateralFactor - 0.5) * width;
      const position = frame.center.clone().addScaledVector(frame.right, lateral);
      const edgeBlend = Math.sin(lateralFactor * Math.PI);
      position.y =
        getCapeTerraceSurfaceHeight(frame, lateral) +
        0.06 +
        edgeBlend *
          (0.34 +
            Math.sin(progressFactor * Math.PI * 13 + lateralFactor * 3) *
              0.13);
      positions.push(position.x, position.y, position.z);
      const color =
        stoneColors[(progressIndex + lateralIndex) % stoneColors.length] ??
        defaultStoneColor;
      colors.push(color.r, color.g, color.b);
    }
  }

  for (let progressIndex = 0; progressIndex < progressSegments; progressIndex += 1) {
    for (let lateralIndex = 0; lateralIndex < lateralSegments; lateralIndex += 1) {
      const current = progressIndex * verticesPerRow + lateralIndex;
      const next = current + verticesPerRow;
      indices.push(current, next, current + 1, current + 1, next, next + 1);
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute("color", new THREE.Float32BufferAttribute(colors, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  geometry.computeBoundingSphere();
  return geometry;
};

const configureMesh = (
  mesh: THREE.Mesh,
  castShadow: boolean,
  receiveShadow: boolean,
): THREE.Mesh => {
  mesh.castShadow = castShadow;
  mesh.receiveShadow = receiveShadow;
  return mesh;
};

const applyFrame = (
  object: THREE.Object3D,
  frame: TrackFrame,
  lateralOffset = 0,
  height = 0,
): void => {
  object.position
    .copy(frame.center)
    .addScaledVector(frame.right, lateralOffset)
    .addScaledVector(frame.normal, height);
  object.quaternion.copy(frame.quaternion);
};

export const getBridgeBeamContactPoint = (
  frame: TrackFrame,
  lateralOffset: number,
  beamCenterHeight: number,
  beamHeight: number,
  target = new THREE.Vector3(),
): THREE.Vector3 =>
  target
    .copy(frame.center)
    .addScaledVector(frame.right, lateralOffset)
    .addScaledVector(frame.normal, beamCenterHeight - beamHeight * 0.5);

export type BridgeBearingPlacement = {
  center: THREE.Vector3;
  contact: THREE.Vector3;
  matrix: THREE.Matrix4;
};

export const getBridgeBearingPlacement = (
  frame: TrackFrame,
  lateralOffset: number,
  beamCenterHeight: number,
  beamHeight: number,
  size: readonly [number, number, number],
): BridgeBearingPlacement => {
  const contact = getBridgeBeamContactPoint(
    frame,
    lateralOffset,
    beamCenterHeight,
    beamHeight,
  );
  const center = contact
    .clone()
    .addScaledVector(frame.normal, size[1] * -0.5);
  const matrix = new THREE.Matrix4().compose(
    center,
    frame.quaternion,
    new THREE.Vector3(...size),
  );
  return { center, contact, matrix };
};

const createRoadBookTexture = (): THREE.CanvasTexture => {
  const canvas = document.createElement("canvas");
  canvas.width = 1_024;
  canvas.height = 256;
  const context = canvas.getContext("2d");
  if (context === null) {
    throw new Error("AURELIA signage requires a 2D canvas context");
  }
  context.fillStyle = "#e8e0d1";
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.fillStyle = "#234a78";
  context.fillRect(0, 0, 54, canvas.height);
  context.fillRect(54, 0, 580, 26);
  context.fillStyle = "#b74732";
  context.fillRect(54, 26, 286, 18);
  context.fillStyle = "#25292b";
  context.font = "800 98px Arial, sans-serif";
  context.letterSpacing = "8px";
  context.fillText("AURELIA", 92, 143);
  context.font = "700 34px Arial, sans-serif";
  context.letterSpacing = "5px";
  context.fillText("SECTOR 01  /  MARSHAL", 98, 202);
  context.fillStyle = "#b74732";
  context.fillRect(882, 0, 142, canvas.height);
  context.fillStyle = "#f3ede2";
  context.font = "900 88px Arial, sans-serif";
  context.letterSpacing = "0px";
  context.fillText("01", 896, 151);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
};

const createCapeAsterTexture = (): THREE.CanvasTexture => {
  const canvas = document.createElement("canvas");
  canvas.width = 1_024;
  canvas.height = 256;
  const context = canvas.getContext("2d");
  if (context === null) {
    throw new Error("CAPE ASTER signage requires a 2D canvas context");
  }
  context.fillStyle = "#20282a";
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.fillStyle = "#d8d0c0";
  context.font = "800 92px Arial, sans-serif";
  context.letterSpacing = "7px";
  context.fillText("CAPE ASTER", 84, 126);
  context.fillStyle = "#bfc8c5";
  context.font = "700 31px Arial, sans-serif";
  context.letterSpacing = "6px";
  context.fillText("WIND PASS  /  SECTOR 01", 88, 190);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 8;
  return texture;
};

export const createRoadPuddleGeometry = (): THREE.BufferGeometry => {
  const radialSegments = 18;
  const positions = [0, 0, 0];
  const uvs = [0.5, 0.5];
  const indices: number[] = [];

  for (let index = 0; index < radialSegments; index += 1) {
    const angle = index / radialSegments * Math.PI * 2;
    const radius = THREE.MathUtils.clamp(
      0.78 +
        Math.sin(angle * 3 + 0.42) * 0.14 +
        Math.sin(angle * 5 - 1.1) * 0.08 +
        Math.sin(angle * 8 + 0.7) * 0.035,
      0.54,
      0.98,
    );
    const x = Math.cos(angle) * radius * 0.64;
    const z = Math.sin(angle) * radius;
    positions.push(x, 0, z);
    uvs.push(0.5 + x * 0.5, 0.5 + z * 0.5);
  }

  for (let index = 0; index < radialSegments; index += 1) {
    const current = index + 1;
    const next = (index + 1) % radialSegments + 1;
    indices.push(0, next, current);
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute(
    "position",
    new THREE.Float32BufferAttribute(positions, 3),
  );
  geometry.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
};

const createRoad = (
  track: SuntraceTrack,
  materials: SuntraceMaterialLibrary,
  geometries: THREE.BufferGeometry[],
  localMaterials: THREE.Material[],
): THREE.Group => {
  const group = new THREE.Group();
  group.name = "Circuit surface";
  const halfWidth = track.width * 0.5;
  const bridgeFasciaMaterial = new THREE.MeshStandardMaterial({
    color: CAPE_ASTER_BRIDGE_FASCIA_RESPONSE.color,
    emissive: CAPE_ASTER_BRIDGE_FASCIA_RESPONSE.emissive,
    emissiveIntensity: CAPE_ASTER_BRIDGE_FASCIA_RESPONSE.emissiveIntensity,
    roughness: CAPE_ASTER_BRIDGE_FASCIA_RESPONSE.roughness,
    metalness: CAPE_ASTER_BRIDGE_FASCIA_RESPONSE.metalness,
    envMapIntensity: CAPE_ASTER_BRIDGE_FASCIA_RESPONSE.envMapIntensity,
    side: THREE.DoubleSide,
  });
  localMaterials.push(bridgeFasciaMaterial);
  const roadGeometry = createStripGeometry(
    track,
    -halfWidth,
    halfWidth,
    0,
    640,
  );
  const leftShoulderGeometry = createStripGeometry(
    track,
    -halfWidth - 2.2,
    -halfWidth,
    -0.02,
    640,
  );
  const rightShoulderGeometry = createStripGeometry(
    track,
    halfWidth,
    halfWidth + 2.2,
    -0.02,
    640,
  );
  const leftDeckWallGeometry = createOffsetWallGeometry(
    track,
    -halfWidth - 2.2,
    -0.82,
    -0.01,
    640,
  );
  const rightDeckWallGeometry = createOffsetWallGeometry(
    track,
    halfWidth + 2.2,
    -0.82,
    -0.01,
    640,
  );
  const leftEdgeLineGeometry = createStripGeometry(
    track,
    -halfWidth + 0.22,
    -halfWidth + 0.4,
    0.025,
    640,
  );
  const rightEdgeLineGeometry = createStripGeometry(
    track,
    halfWidth - 0.4,
    halfWidth - 0.22,
    0.025,
    640,
  );
  const leftDrainGeometry = createStripGeometry(
    track,
    -halfWidth - 1.12,
    -halfWidth - 0.78,
    0.012,
    640,
  );
  const rightDrainGeometry = createStripGeometry(
    track,
    halfWidth + 0.78,
    halfWidth + 1.12,
    0.012,
    640,
  );
  geometries.push(
    roadGeometry,
    leftShoulderGeometry,
    rightShoulderGeometry,
    leftDeckWallGeometry,
    rightDeckWallGeometry,
    leftEdgeLineGeometry,
    rightEdgeLineGeometry,
    leftDrainGeometry,
    rightDrainGeometry,
  );
  group.add(
    configureMesh(new THREE.Mesh(roadGeometry, materials.asphalt), false, true),
    configureMesh(
      new THREE.Mesh(leftShoulderGeometry, materials.shoulder),
      false,
      true,
    ),
    configureMesh(
      new THREE.Mesh(rightShoulderGeometry, materials.shoulder),
      false,
      true,
    ),
    configureMesh(
      new THREE.Mesh(leftDeckWallGeometry, bridgeFasciaMaterial),
      true,
      true,
    ),
    configureMesh(
      new THREE.Mesh(rightDeckWallGeometry, bridgeFasciaMaterial),
      true,
      true,
    ),
    configureMesh(
      new THREE.Mesh(leftEdgeLineGeometry, materials.roadMarking),
      false,
      true,
    ),
    configureMesh(
      new THREE.Mesh(rightEdgeLineGeometry, materials.roadMarking),
      false,
      true,
    ),
    configureMesh(new THREE.Mesh(leftDrainGeometry, materials.asphalt), false, true),
    configureMesh(new THREE.Mesh(rightDrainGeometry, materials.asphalt), false, true),
  );

  const markingGeometry = new THREE.BoxGeometry(0.13, 0.01, 1.9);
  const rumbleGeometry = new THREE.BoxGeometry(0.5, 0.045, 1.3);
  const puddleGeometry = createRoadPuddleGeometry();
  const drainBarGeometry = new THREE.BoxGeometry(0.035, 0.016, 0.64);
  const roadStudGeometry = new THREE.BoxGeometry(0.12, 0.028, 0.2);
  geometries.push(
    markingGeometry,
    rumbleGeometry,
    puddleGeometry,
    drainBarGeometry,
    roadStudGeometry,
  );
  const centerMarkings = new THREE.InstancedMesh(
    markingGeometry,
    materials.roadMarking,
    288,
  );
  const whiteRumble = new THREE.InstancedMesh(
    rumbleGeometry,
    materials.roadMarking,
    80,
  );
  const vermilionRumble = new THREE.InstancedMesh(
    rumbleGeometry,
    materials.safety,
    80,
  );
  const puddles = new THREE.InstancedMesh(
    puddleGeometry,
    materials.puddle,
    56,
  );
  const drainBars = new THREE.InstancedMesh(
    drainBarGeometry,
    materials.guardrail,
    2_240,
  );
  const whiteRoadStuds = new THREE.InstancedMesh(
    roadStudGeometry,
    materials.roadMarking,
    384,
  );
  const redRoadStuds = new THREE.InstancedMesh(
    roadStudGeometry,
    materials.safety,
    384,
  );
  const frame = track.sample(0);
  const dummy = new THREE.Object3D();

  for (let index = 0; index < 288; index += 1) {
    const markingProgress = index / 288 +
      CAPE_ASTER_START_CENTER_MARKING_CONTRACT.progressOffset;
    const wrappedMarkingProgress = markingProgress % 1;
    track.sample(
      markingProgress,
      0,
      frame,
    );
    applyFrame(dummy, frame, 0, 0.052);
    const startClearance =
      wrappedMarkingProgress < CAPE_ASTER_START_CENTER_MARKING_CONTRACT.clearanceProgress ||
      wrappedMarkingProgress > 1 - CAPE_ASTER_START_CENTER_MARKING_CONTRACT.clearanceProgress;
    dummy.scale.set(startClearance ? 0 : 1, startClearance ? 0 : 1, startClearance ? 0 : 1);
    dummy.updateMatrix();
    centerMarkings.setMatrixAt(index, dummy.matrix);
  }

  let whiteIndex = 0;
  let vermilionIndex = 0;
  for (let index = 0; index < 160; index += 1) {
    const progress = index / 160;
    track.sample(progress, 0, frame);
    const side = index % 2 === 0 ? -1 : 1;
    applyFrame(dummy, frame, side * (halfWidth + 0.52), 0.065);
    dummy.updateMatrix();
    if (Math.floor(index / 2) % 2 === 0) {
      whiteRumble.setMatrixAt(whiteIndex, dummy.matrix);
      whiteIndex += 1;
    } else {
      vermilionRumble.setMatrixAt(vermilionIndex, dummy.matrix);
      vermilionIndex += 1;
    }
  }

  centerMarkings.instanceMatrix.needsUpdate = true;
  whiteRumble.instanceMatrix.needsUpdate = true;
  vermilionRumble.instanceMatrix.needsUpdate = true;
  const puddleRandom = createRandom(0x9e3779b9);
  for (let index = 0; index < 56; index += 1) {
    track.sample((index + 0.36) / 56, 0, frame);
    applyFrame(
      dummy,
      frame,
      (puddleRandom() - 0.5) * (track.width - 3.2),
      0.045,
    );
    dummy.scale.set(
      0.36 + puddleRandom() * 0.28,
      1,
      0.56 + puddleRandom() * 0.38,
    );
    dummy.updateMatrix();
    puddles.setMatrixAt(index, dummy.matrix);
  }
  puddles.instanceMatrix.needsUpdate = true;
  let drainBarIndex = 0;
  for (let index = 0; index < 224; index += 1) {
    track.sample((index + 0.47) / 224, 0, frame);
    for (const side of [-1, 1]) {
      const channelOffset = side * (halfWidth + 0.95);
      for (let bar = -2; bar <= 2; bar += 1) {
        applyFrame(dummy, frame, channelOffset + side * bar * 0.095, 0.027);
        dummy.scale.set(1, 1, 1);
        dummy.updateMatrix();
        drainBars.setMatrixAt(drainBarIndex, dummy.matrix);
        drainBarIndex += 1;
      }
    }
  }
  drainBars.instanceMatrix.needsUpdate = true;
  let whiteStudIndex = 0;
  let redStudIndex = 0;
  for (let index = 0; index < 384; index += 1) {
    track.sample((index + 0.31) / 384, 0, frame);
    for (let sideIndex = 0; sideIndex < 2; sideIndex += 1) {
      const side = sideIndex === 0 ? -1 : 1;
      applyFrame(dummy, frame, side * (halfWidth - 0.64), 0.046);
      dummy.scale.set(1, 1, 1);
      dummy.updateMatrix();
      if ((index + sideIndex) % 2 === 0) {
        whiteRoadStuds.setMatrixAt(whiteStudIndex, dummy.matrix);
        whiteStudIndex += 1;
      } else {
        redRoadStuds.setMatrixAt(redStudIndex, dummy.matrix);
        redStudIndex += 1;
      }
    }
  }
  whiteRoadStuds.instanceMatrix.needsUpdate = true;
  redRoadStuds.instanceMatrix.needsUpdate = true;
  puddles.renderOrder = 1;
  centerMarkings.receiveShadow = true;
  whiteRumble.receiveShadow = true;
  vermilionRumble.receiveShadow = true;
  drainBars.castShadow = true;
  drainBars.receiveShadow = true;
  whiteRoadStuds.castShadow = true;
  redRoadStuds.castShadow = true;
  group.add(
    centerMarkings,
    whiteRumble,
    vermilionRumble,
    puddles,
    drainBars,
    whiteRoadStuds,
    redRoadStuds,
  );
  return group;
};

type CoastalRouteMarkerPlacement = {
  progress: number;
  side: -1 | 1;
  lateral: number;
  scale: number;
  yaw: number;
};

const createCapeHeroChapter = (
  track: SuntraceTrack,
  materials: SuntraceMaterialLibrary,
  geometries: THREE.BufferGeometry[],
  localMaterials: THREE.Material[],
  localTextures: THREE.Texture[],
): THREE.Group => {
  const group = new THREE.Group();
  group.name = "Cape Aster wind pass";
  const placements = [
    { progress: 0.078, side: -1, lateral: 11.15, scale: 1.84, yaw: -0.12 },
    { progress: 0.078, side: 1, lateral: 10.75, scale: 1.68, yaw: 0.1 },
  ] satisfies readonly CoastalRouteMarkerPlacement[];

  const plinthGeometry = new THREE.CylinderGeometry(0.42, 0.66, 1.2, 6);
  const bladeShape = new THREE.Shape();
  bladeShape.moveTo(-0.5, -1.3);
  bladeShape.lineTo(0.38, -1.3);
  bladeShape.lineTo(0.27, 0.91);
  bladeShape.lineTo(-0.08, 1.36);
  bladeShape.lineTo(-0.46, 0.42);
  bladeShape.closePath();
  const bladeCoreGeometry = new THREE.ExtrudeGeometry(bladeShape, {
    depth: 0.26,
    bevelEnabled: true,
    bevelSegments: 1,
    bevelSize: 0.045,
    bevelThickness: 0.045,
    steps: 1,
  });
  bladeCoreGeometry.center();
  const bladeFrontGeometry = new THREE.ShapeGeometry(bladeShape, 8);
  bladeFrontGeometry.center();
  bladeFrontGeometry.translate(0, 0, 0.151);
  const bladeReverseGeometry = new THREE.ShapeGeometry(bladeShape, 8);
  bladeReverseGeometry.center();
  bladeReverseGeometry.rotateY(Math.PI);
  bladeReverseGeometry.translate(0, 0, -0.151);
  const inlayShape = new THREE.Shape();
  inlayShape.moveTo(-0.24, -1.08);
  inlayShape.lineTo(0.14, -1.08);
  inlayShape.lineTo(0.22, 0.78);
  inlayShape.lineTo(-0.05, 1.08);
  inlayShape.lineTo(-0.22, 0.28);
  inlayShape.closePath();
  const inlayGeometry = new THREE.ExtrudeGeometry(inlayShape, {
    depth: 0.38,
    bevelEnabled: false,
    steps: 1,
  });
  inlayGeometry.center();
  geometries.push(
    plinthGeometry,
    bladeCoreGeometry,
    bladeFrontGeometry,
    bladeReverseGeometry,
    inlayGeometry,
  );
  const windSailDarkMaterial = new THREE.MeshStandardMaterial({
    color: 0x4b5859,
    emissive: 0x20292a,
    emissiveIntensity: 0.26,
    roughness: 0.76,
    metalness: 0.08,
    envMapIntensity: 0.68,
  });
  localMaterials.push(windSailDarkMaterial);
  const plinths = new THREE.InstancedMesh(
    plinthGeometry,
    materials.limestoneDark,
    placements.length,
  );
  const bladeCores = new THREE.InstancedMesh(
    bladeCoreGeometry,
    windSailDarkMaterial,
    placements.length,
  );
  const bladeFronts = new THREE.InstancedMesh(
    bladeFrontGeometry,
    materials.limestone,
    placements.length,
  );
  const bladeReverses = new THREE.InstancedMesh(
    bladeReverseGeometry,
    windSailDarkMaterial,
    placements.length,
  );
  const inlays = new THREE.InstancedMesh(
    inlayGeometry,
    materials.safety,
    placements.length,
  );
  plinths.name = "Cape gate plinths";
  bladeCores.name = "Cape gate wind sail cores";
  bladeFronts.name = "Cape gate pale windward faces";
  bladeReverses.name = "Cape gate dark leeward faces";
  inlays.name = "Cape gate vermilion both-face inlays";
  const frame = track.sample(0);
  const dummy = new THREE.Object3D();
  for (let index = 0; index < placements.length; index += 1) {
    const placement = placements[index];
    if (placement === undefined) {
      continue;
    }
    track.samplePlacement(placement.progress, 0, frame);
    const lateral = placement.side * placement.lateral;
    const baseHeight = 0;

    applyFrame(
      dummy,
      frame,
      lateral,
      baseHeight + 0.6 * placement.scale,
    );
    dummy.rotateY(Math.PI + placement.yaw);
    dummy.scale.set(placement.scale, placement.scale, placement.scale);
    dummy.updateMatrix();
    plinths.setMatrixAt(index, dummy.matrix);

    applyFrame(
      dummy,
      frame,
      lateral,
      baseHeight + 2.53 * placement.scale,
    );
    dummy.rotateY(Math.PI + placement.yaw);
    dummy.scale.set(placement.scale, placement.scale, placement.scale);
    dummy.updateMatrix();
    bladeCores.setMatrixAt(index, dummy.matrix);
    bladeFronts.setMatrixAt(index, dummy.matrix);
    bladeReverses.setMatrixAt(index, dummy.matrix);
    inlays.setMatrixAt(index, dummy.matrix);
  }
  for (const markerMesh of [
    plinths,
    bladeCores,
    bladeFronts,
    bladeReverses,
    inlays,
  ]) {
    markerMesh.instanceMatrix.needsUpdate = true;
    markerMesh.castShadow = markerMesh === plinths || markerMesh === bladeCores;
    markerMesh.receiveShadow = true;
    markerMesh.computeBoundingSphere();
  }
  group.add(plinths, bladeCores, bladeFronts, bladeReverses, inlays);

  const plantedTerraceGeometry = createCapeTerraceGeometry(
    track,
    CAPE_TERRACE_OUTER_OFFSET,
    CAPE_TERRACE_INNER_OFFSET,
    0.02,
  );
  const terraceRetainingWallGeometry =
    createCapeTerraceRetainingWallGeometry(track);
  const terraceOuterCopingGeometry = createCapeTerraceGeometry(
    track,
    -36.25,
    -35.25,
    0.09,
  );
  const terraceWallGeometry = createOffsetWallGeometry(
    track,
    -10.22,
    -1.45,
    1.34,
    52,
    CAPE_TERRACE_START,
    CAPE_TERRACE_END,
  );
  const terraceBandGeometry = createOffsetWallGeometry(
    track,
    -10.14,
    0.86,
    1.16,
    52,
    CAPE_TERRACE_START,
    CAPE_TERRACE_END,
  );
  const terraceTopGeometry = createStripGeometry(
    track,
    -13.58,
    -10.2,
    1.34,
    52,
    CAPE_TERRACE_START,
    CAPE_TERRACE_END,
  );
  geometries.push(
    plantedTerraceGeometry,
    terraceRetainingWallGeometry,
    terraceOuterCopingGeometry,
    terraceWallGeometry,
    terraceBandGeometry,
    terraceTopGeometry,
  );
  const plantedTerrace = configureMesh(
    new THREE.Mesh(plantedTerraceGeometry, materials.terrain),
    false,
    true,
  );
  const terraceRetainingWall = configureMesh(
    new THREE.Mesh(terraceRetainingWallGeometry, materials.limestoneDark),
    true,
    true,
  );
  const terraceOuterCoping = configureMesh(
    new THREE.Mesh(terraceOuterCopingGeometry, materials.limestone),
    false,
    true,
  );
  const terraceWall = configureMesh(
    new THREE.Mesh(terraceWallGeometry, materials.limestoneDark),
    true,
    true,
  );
  const terraceBand = configureMesh(
    new THREE.Mesh(terraceBandGeometry, materials.safety),
    false,
    true,
  );
  const terraceTop = configureMesh(
    new THREE.Mesh(terraceTopGeometry, materials.limestone),
    false,
    true,
  );
  terraceWall.name = "Cape Aster terrace wall";
  terraceBand.name = "Cape Aster terrace vermilion band";
  terraceTop.name = "Cape Aster terrace coping";
  plantedTerrace.name = "Cape Aster continuous planted terrace";
  terraceRetainingWall.name = "Cape Aster planted terrace retaining wall";
  terraceRetainingWall.visible =
    CAPE_ASTER_TERRACE_SURFACE_CONTRACT.retainingWallVisible;
  terraceWall.visible = CAPE_ASTER_TERRACE_SURFACE_CONTRACT.terraceWallVisible;
  terraceTop.visible = CAPE_ASTER_TERRACE_SURFACE_CONTRACT.copingVisible;
  terraceOuterCoping.name = "Cape Aster planted terrace outer coping";
  group.add(
    plantedTerrace,
    terraceRetainingWall,
    terraceOuterCoping,
    terraceWall,
    terraceBand,
    terraceTop,
  );

  const signTexture = createCapeAsterTexture();
  const signMaterial = new THREE.MeshStandardMaterial({
    color: 0xffffff,
    map: signTexture,
    emissive: 0x2c1a13,
    emissiveMap: signTexture,
    emissiveIntensity: 0.12,
    roughness: 0.52,
    metalness: 0.02,
  });
  localMaterials.push(signMaterial);
  localTextures.push(signTexture);
  const signBackGeometry = new THREE.BoxGeometry(6.8, 1.72, 0.24);
  const signFaceGeometry = new THREE.PlaneGeometry(6.48, 1.42);
  const signPostGeometry = new THREE.BoxGeometry(0.3, 3.9, 0.3);
  const signBladeGeometry = new THREE.BoxGeometry(0.34, 2.85, 0.34);
  geometries.push(
    signBackGeometry,
    signFaceGeometry,
    signPostGeometry,
    signBladeGeometry,
  );
  const signRoot = new THREE.Group();
  signRoot.name = "Cape Aster wind pass sign";
  const signPlacement = getCapeAsterSignPlacement(track);
  signRoot.position.copy(signPlacement.position);
  signRoot.quaternion.copy(signPlacement.quaternion);
  signRoot.rotateY(-0.08);
  const signBack = configureMesh(
    new THREE.Mesh(signBackGeometry, materials.limestoneDark),
    true,
    true,
  );
  signBack.position.y = 4.35;
  const signFace = new THREE.Mesh(signFaceGeometry, signMaterial);
  signFace.position.set(0, 4.35, -0.131);
  signFace.rotation.y = Math.PI;
  signRoot.add(signBack, signFace);
  for (const lateral of [-2.72, 2.72]) {
    const post = configureMesh(
      new THREE.Mesh(signPostGeometry, materials.limestoneDark),
      true,
      true,
    );
    post.position.set(lateral, 1.95, 0);
    signRoot.add(post);
  }
  const signBlade = configureMesh(
    new THREE.Mesh(signBladeGeometry, materials.safety),
    true,
    true,
  );
  signBlade.position.set(-3.03, 2.15, -0.06);
  signBlade.rotation.z = -0.1;
  signRoot.add(signBlade);
  group.add(signRoot);

  const architectureBoxGeometry = new THREE.BoxGeometry(1, 1, 1);
  geometries.push(architectureBoxGeometry);
  const limestoneMatrices: THREE.Matrix4[] = [];
  const darkMatrices: THREE.Matrix4[] = [];
  const safetyMatrices: THREE.Matrix4[] = [];
  const lightMatrices: THREE.Matrix4[] = [];
  const trackRoot = new THREE.Object3D();
  const box = new THREE.Object3D();
  const appendBoxMatrix = (
    target: THREE.Matrix4[],
    root: THREE.Object3D,
    position: readonly [number, number, number],
    scale: readonly [number, number, number],
    rotation: readonly [number, number, number] = [0, 0, 0],
  ): void => {
    box.position.set(...position);
    box.rotation.set(...rotation);
    box.scale.set(...scale);
    box.updateMatrix();
    target.push(new THREE.Matrix4().multiplyMatrices(root.matrix, box.matrix));
  };

  for (const placement of placements) {
    track.samplePlacement(placement.progress, 0, frame);
    const lateral = placement.side * placement.lateral;
    const baseHeight = 0;
    applyFrame(trackRoot, frame, lateral, baseHeight);
    trackRoot.rotateY(Math.PI + placement.yaw);
    trackRoot.updateMatrix();
    appendBoxMatrix(
      limestoneMatrices,
      trackRoot,
      [0, 0.12, 0],
      [3.1, 0.24, 5.1],
    );
    appendBoxMatrix(
      safetyMatrices,
      trackRoot,
      [-placement.side * 1.43, 0.26, 0],
      [0.2, 0.28, 4.54],
    );
    appendBoxMatrix(
      darkMatrices,
      trackRoot,
      [-0.62, 0.7, 0],
      [0.18, 1.38, 0.38],
      [0, 0, 0.25],
    );
    appendBoxMatrix(
      darkMatrices,
      trackRoot,
      [0.62, 0.7, 0],
      [0.18, 1.38, 0.38],
      [0, 0, -0.25],
    );
  }

  const serviceRoot = new THREE.Group();
  const serviceLateral = -18;
  track.samplePlacement(0.156, 0, frame);
  applyFrame(serviceRoot, frame, serviceLateral, 0);
  serviceRoot.position.y =
    getCapeTerraceSurfaceHeight(frame, serviceLateral) + 0.04;
  serviceRoot.updateMatrix();
  appendBoxMatrix(
    limestoneMatrices,
    serviceRoot,
    [0, -0.62, 0],
    [13, 1.24, 41],
  );
  appendBoxMatrix(
    limestoneMatrices,
    serviceRoot,
    [4.25, 0.12, 0],
    [4.45, 0.24, 31.5],
  );
  appendBoxMatrix(
    limestoneMatrices,
    serviceRoot,
    [-2.2, 2.72, 0],
    [8.55, 5.44, 30.4],
  );
  appendBoxMatrix(
    limestoneMatrices,
    serviceRoot,
    [-6.15, 0.18, 4.2],
    [1.25, 0.36, 12.8],
  );
  for (let index = 0; index < 4; index += 1) {
    const stepHeight = 0.13 + index * 0.12;
    appendBoxMatrix(
      limestoneMatrices,
      serviceRoot,
      [5.72 - index * 0.56, stepHeight * 0.5, -10.4],
      [1.25, stepHeight, 3.6 + index * 0.46],
    );
  }
  for (const z of [-10.4, 0, 10.4]) {
    appendBoxMatrix(
      darkMatrices,
      serviceRoot,
      [2.16, 1.42, z],
      [0.18, 2.55, 1.65],
    );
    appendBoxMatrix(
      safetyMatrices,
      serviceRoot,
      [2.28, 1.42, z - 1.02],
      [0.12, 2.55, 0.12],
    );
    appendBoxMatrix(
      lightMatrices,
      serviceRoot,
      [2.3, 2.92, z],
      [0.1, 0.12, 0.78],
    );
  }
  for (const z of [-11.2, -5.6, 0, 5.6, 11.2]) {
    appendBoxMatrix(
      darkMatrices,
      serviceRoot,
      [2.18, 4.2, z],
      [0.16, 1.15, 4.35],
    );
  }
  appendBoxMatrix(
    safetyMatrices,
    serviceRoot,
    [2.32, 3.46, 0],
    [0.16, 0.26, 29.2],
  );
  appendBoxMatrix(
    darkMatrices,
    serviceRoot,
    [6.18, 1.18, 0],
    [0.1, 0.12, 29.2],
  );
  for (const z of [-13.8, -9.2, -4.6, 0, 4.6, 9.2, 13.8]) {
    appendBoxMatrix(
      darkMatrices,
      serviceRoot,
      [6.18, 0.62, z],
      [0.1, 1.12, 0.1],
    );
  }
  for (const z of [-12.8, -6.4, 0, 6.4, 12.8]) {
    appendBoxMatrix(
      lightMatrices,
      serviceRoot,
      [6.08, 0.58, z],
      [0.08, 0.12, 0.58],
    );
  }
  const roofProfile = new THREE.Shape();
  roofProfile.moveTo(-6.5, 0);
  roofProfile.lineTo(5.1, 0);
  roofProfile.lineTo(4.45, 0.62);
  roofProfile.lineTo(-3.85, 2.18);
  roofProfile.lineTo(-6.5, 0.74);
  roofProfile.closePath();
  const stationRoofGeometry = new THREE.ExtrudeGeometry(roofProfile, {
    depth: 30.8,
    bevelEnabled: true,
    bevelSegments: 1,
    bevelSize: 0.12,
    bevelThickness: 0.12,
    steps: 1,
  });
  stationRoofGeometry.center();
  geometries.push(stationRoofGeometry);
  const stationRoof = configureMesh(
    new THREE.Mesh(stationRoofGeometry, materials.limestone),
    true,
    true,
  );
  stationRoof.name = "Cape Aster folded wind roof";
  stationRoof.position.set(-0.7, 6.52, 0);
  serviceRoot.add(stationRoof);
  group.add(serviceRoot);

  const asterHalo = createAsterHaloLandmark(
    {
      limestone: materials.limestone,
      safety: materials.safety,
      steel: windSailDarkMaterial,
    },
    geometries,
  );
  const haloPlacement = getAsterHaloPlacement(track);
  asterHalo.position.copy(haloPlacement.position);
  asterHalo.quaternion.copy(haloPlacement.quaternion);
  const coastalSetPieces = createCapeAsterCoastalSetPieces(
    track,
    {
      limestone: materials.limestone,
      limestoneDark: materials.limestoneDark,
      safety: materials.safety,
      steel: materials.guardrail,
    },
    geometries,
  );
  group.add(asterHalo, coastalSetPieces);

  for (const form of ASTER_HALO_ROADSIDE_FORMS) {
    const placement = getUprightRoadsidePlacement(
      track,
      form.progress,
      form.lateral,
    );
    trackRoot.position.copy(placement.position);
    trackRoot.quaternion.copy(placement.quaternion);
    trackRoot.updateMatrix();
    appendBoxMatrix(
      limestoneMatrices,
      trackRoot,
      [0, 0.11, 0],
      [form.halfWidth * 2.25, 0.22, 1.24],
    );
    if (form.kind === "blade") {
      appendBoxMatrix(
        darkMatrices,
        trackRoot,
        [-0.16, form.height * 0.5, 0],
        [0.24, form.height, 0.3],
        [0, 0, -0.09],
      );
      appendBoxMatrix(
        safetyMatrices,
        trackRoot,
        [0.18, form.height * 0.66, 0.02],
        [0.22, form.height * 0.62, 0.14],
        [0, 0, 0.19],
      );
    } else if (form.kind === "aperture") {
      for (const side of [-1, 1]) {
        appendBoxMatrix(
          limestoneMatrices,
          trackRoot,
          [side * form.halfWidth * 0.7, form.height * 0.43, 0],
          [0.24, form.height * 0.86, 0.36],
          [0, 0, side * -0.08],
        );
      }
      appendBoxMatrix(
        darkMatrices,
        trackRoot,
        [0, form.height * 0.87, 0],
        [form.halfWidth * 1.58, 0.26, 0.42],
      );
      appendBoxMatrix(
        safetyMatrices,
        trackRoot,
        [0, form.height * 0.58, 0.22],
        [0.12, form.height * 0.34, 0.08],
      );
    } else if (form.kind === "sail") {
      appendBoxMatrix(
        darkMatrices,
        trackRoot,
        [-0.2, form.height * 0.5, 0],
        [0.2, form.height, 0.28],
        [0, 0, -0.05],
      );
      appendBoxMatrix(
        limestoneMatrices,
        trackRoot,
        [0.18, form.height * 0.68, 0],
        [0.58, form.height * 0.56, 0.18],
        [0, 0, 0.2],
      );
      appendBoxMatrix(
        safetyMatrices,
        trackRoot,
        [0.38, form.height * 0.76, 0.12],
        [0.12, form.height * 0.42, 0.08],
        [0, 0, 0.2],
      );
    } else {
      appendBoxMatrix(
        limestoneMatrices,
        trackRoot,
        [-0.12, form.height * 0.5, 0],
        [form.halfWidth * 1.38, form.height, 0.42],
        [0, 0, -0.04],
      );
      appendBoxMatrix(
        darkMatrices,
        trackRoot,
        [form.halfWidth * 0.5, form.height * 0.42, 0.24],
        [0.16, form.height * 0.66, 0.1],
      );
      appendBoxMatrix(
        safetyMatrices,
        trackRoot,
        [-0.06, form.height * 0.86, 0.24],
        [form.halfWidth * 1.14, 0.16, 0.08],
      );
    }
  }

  for (const side of [-1, 1]) {
    const support = getCapeAsterGatewaySupportPlacement(
      track,
      side * CAPE_ASTER_GATEWAY_ENVELOPE.supportLateral,
    );
    trackRoot.position.copy(support.foundation);
    trackRoot.quaternion.copy(support.quaternion);
    trackRoot.updateMatrix();
    appendBoxMatrix(
      limestoneMatrices,
      trackRoot,
      [0, 0.08, 0],
      [1.5, 0.16, 2.25],
    );
  }
  track.samplePlacement(CAPE_ASTER_GATEWAY_ENVELOPE.progress, 0, frame);
  applyFrame(trackRoot, frame, 0, 0);
  trackRoot.updateMatrix();
  appendBoxMatrix(
    safetyMatrices,
    trackRoot,
    [0, 6.21, -1.94],
    [22.45, 0.12, 0.14],
  );
  for (const lateral of [-5.4, 0, 5.4]) {
    appendBoxMatrix(
      lightMatrices,
      trackRoot,
      [lateral, 6.19, -1.82],
      [0.74, 0.08, 0.12],
    );
  }
  const capeGatewayGeometry = createCapeAsterGatewayGeometry(track);
  geometries.push(capeGatewayGeometry);
  const capeGateway = configureMesh(
    new THREE.Mesh(capeGatewayGeometry, windSailDarkMaterial),
    true,
    true,
  );
  capeGateway.name = "Cape Aster road-wrapping maintenance gallery";
  group.add(capeGateway);

  const serviceLightMaterial = new THREE.MeshStandardMaterial({
    color: 0xffd5a2,
    emissive: 0xff8f4c,
    emissiveIntensity: 1.65,
    roughness: 0.32,
    metalness: 0.04,
  });
  localMaterials.push(serviceLightMaterial);
  const createArchitectureMesh = (
    matrices: readonly THREE.Matrix4[],
    material: THREE.Material,
    name: string,
    castShadow: boolean,
  ): THREE.InstancedMesh => {
    const mesh = new THREE.InstancedMesh(
      architectureBoxGeometry,
      material,
      matrices.length,
    );
    mesh.name = name;
    matrices.forEach((matrix, index) => mesh.setMatrixAt(index, matrix));
    mesh.instanceMatrix.needsUpdate = true;
    mesh.castShadow = castShadow;
    mesh.receiveShadow = true;
    mesh.computeBoundingSphere();
    return mesh;
  };
  group.add(
    createArchitectureMesh(
      limestoneMatrices,
      materials.limestone,
      "Cape Aster grounded operations hall and access court",
      true,
    ),
    createArchitectureMesh(
      darkMatrices,
      windSailDarkMaterial,
      "Cape Aster glazing doors railings and wind frame",
      true,
    ),
    createArchitectureMesh(
      safetyMatrices,
      materials.safety,
      "Cape Aster service thresholds and identity band",
      false,
    ),
    createArchitectureMesh(
      lightMatrices,
      serviceLightMaterial,
      "Cape Aster human-scale integrated wayfinding lights",
      false,
    ),
  );
  return group;
};

const createGuardrails = (
  track: SuntraceTrack,
  materials: SuntraceMaterialLibrary,
  geometries: THREE.BufferGeometry[],
): THREE.Group => {
  const group = new THREE.Group();
  group.name = "Galvanized circuit barriers";
  const halfWidth = track.width * 0.5;
  const beamRanges: readonly [number, number][] = [
    [0.36, 0.54],
    [0.76, 1.02],
  ];

  for (const side of [-1, 1]) {
    const innerOffset = side * (halfWidth + 1.28);
    const outerOffset = side * (halfWidth + 1.5);
    for (const [bottom, top] of beamRanges) {
      const innerWall = createOffsetWallGeometry(track, innerOffset, bottom, top, 512);
      const outerWall = createOffsetWallGeometry(track, outerOffset, bottom, top, 512);
      const topSurface = createStripGeometry(
        track,
        Math.min(innerOffset, outerOffset),
        Math.max(innerOffset, outerOffset),
        top,
        512,
      );
      geometries.push(innerWall, outerWall, topSurface);
      group.add(
        configureMesh(new THREE.Mesh(innerWall, materials.guardrailInner), true, true),
        configureMesh(new THREE.Mesh(outerWall, materials.guardrail), true, true),
        configureMesh(new THREE.Mesh(topSurface, materials.guardrail), true, true),
      );
    }
  }

  const postGeometry = new THREE.BoxGeometry(0.18, 1.25, 0.16);
  const reflectorGeometry = new THREE.BoxGeometry(0.36, 0.22, 0.08);
  geometries.push(postGeometry, reflectorGeometry);
  const posts = new THREE.InstancedMesh(postGeometry, materials.guardrail, 576);
  const whiteReflectors = new THREE.InstancedMesh(
    reflectorGeometry,
    materials.roadMarking,
    288,
  );
  const redReflectors = new THREE.InstancedMesh(
    reflectorGeometry,
    materials.safety,
    288,
  );
  const frame = track.sample(0);
  const dummy = new THREE.Object3D();
  let whiteReflectorIndex = 0;
  let redReflectorIndex = 0;
  for (let index = 0; index < 288; index += 1) {
    const progress = index / 288;
    track.sample(progress, 0, frame);
    for (let sideIndex = 0; sideIndex < 2; sideIndex += 1) {
      const side = sideIndex === 0 ? -1 : 1;
      applyFrame(dummy, frame, side * (halfWidth + 1.42), 0.54);
      dummy.updateMatrix();
      posts.setMatrixAt(index * 2 + sideIndex, dummy.matrix);
      applyFrame(dummy, frame, side * (halfWidth + 1.255), 0.88);
      dummy.updateMatrix();
      if ((index + sideIndex) % 2 === 0) {
        whiteReflectors.setMatrixAt(whiteReflectorIndex, dummy.matrix);
        whiteReflectorIndex += 1;
      } else {
        redReflectors.setMatrixAt(redReflectorIndex, dummy.matrix);
        redReflectorIndex += 1;
      }
    }
  }
  posts.instanceMatrix.needsUpdate = true;
  whiteReflectors.instanceMatrix.needsUpdate = true;
  redReflectors.instanceMatrix.needsUpdate = true;
  posts.castShadow = true;
  posts.receiveShadow = true;
  whiteReflectors.castShadow = true;
  redReflectors.castShadow = true;
  group.add(posts, whiteReflectors, redReflectors);
  return group;
};

const createElevatedSupports = (
  track: SuntraceTrack,
  materials: SuntraceMaterialLibrary,
  geometries: THREE.BufferGeometry[],
  localMaterials: THREE.Material[],
): THREE.Group => {
  const group = new THREE.Group();
  group.name = "Cliff viaduct supports";
  const supportFrames = Array.from({ length: 72 }, (_, index) => {
    const progress = index / 72;
    const frame = track.sample(progress);
    const outsideCapeSupports = progress < 0.068 || progress > 0.14;
    return frame.center.y > 22 &&
      (progress < 0.555 || progress > 0.675) &&
      outsideCapeSupports
      ? { progress, height: frame.center.y }
      : null;
  }).filter((entry): entry is { progress: number; height: number } => entry !== null);
  const capeSupportFrames = [0.074, 0.086, 0.098, 0.11, 0.122, 0.134, 0.146].map(
    (progress) => ({ progress, height: track.sample(progress).center.y }),
  );
  const pillarGeometry = new THREE.CylinderGeometry(
    CAPE_ASTER_SUPPORT_PILLAR_CONTRACT.topRadius,
    CAPE_ASTER_SUPPORT_PILLAR_CONTRACT.bottomRadius,
    1,
    CAPE_ASTER_SUPPORT_PILLAR_CONTRACT.radialSegments,
  );
  pillarGeometry.rotateY(CAPE_ASTER_SUPPORT_PILLAR_CONTRACT.rotation);
  const beamGeometry = new THREE.BoxGeometry(track.width + 5.2, 0.72, 1.55);
  const braceGeometry = new THREE.CylinderGeometry(
    CAPE_ASTER_SUPPORT_BRACING_CONTRACT.thickness,
    CAPE_ASTER_SUPPORT_BRACING_CONTRACT.thickness,
    1,
    CAPE_ASTER_SUPPORT_BRACING_CONTRACT.radialSegments,
  );
  const capeGirderGeometry = createCapeAsterBridgeGirderGeometry(track);
  geometries.push(pillarGeometry, beamGeometry, braceGeometry, capeGirderGeometry);
  const capeSteelMaterial = new THREE.MeshStandardMaterial({
    color: CAPE_ASTER_SUPPORT_STEEL_RESPONSE.color,
    emissive: CAPE_ASTER_SUPPORT_STEEL_RESPONSE.emissive,
    emissiveIntensity: CAPE_ASTER_SUPPORT_STEEL_RESPONSE.emissiveIntensity,
    roughness: CAPE_ASTER_SUPPORT_STEEL_RESPONSE.roughness,
    metalness: CAPE_ASTER_SUPPORT_STEEL_RESPONSE.metalness,
    envMapIntensity: CAPE_ASTER_SUPPORT_STEEL_RESPONSE.envMapIntensity,
  });
  const capeConcreteMaterial = new THREE.MeshStandardMaterial({
    color: CAPE_ASTER_SUPPORT_MATERIAL_RESPONSE.concreteColor,
    map: materials.limestoneDark.map,
    bumpMap: materials.limestoneDark.bumpMap,
    bumpScale: 0.08,
    roughnessMap: materials.limestoneDark.roughnessMap,
    emissive: CAPE_ASTER_SUPPORT_MATERIAL_RESPONSE.concreteEmissive,
    emissiveIntensity:
      CAPE_ASTER_SUPPORT_MATERIAL_RESPONSE.concreteEmissiveIntensity,
    roughness: CAPE_ASTER_SUPPORT_MATERIAL_RESPONSE.concreteRoughness,
    metalness: 0.01,
    envMapIntensity: CAPE_ASTER_SUPPORT_MATERIAL_RESPONSE.concreteEnvMapIntensity,
  });
  localMaterials.push(capeSteelMaterial, capeConcreteMaterial);
  const pillars = new THREE.InstancedMesh(
    pillarGeometry,
    capeConcreteMaterial,
    supportFrames.length * 2,
  );
  const beams = new THREE.InstancedMesh(
    beamGeometry,
    capeConcreteMaterial,
    supportFrames.length,
  );
  const bearings = new THREE.InstancedMesh(
    pillarGeometry,
    capeConcreteMaterial,
    supportFrames.length * 2,
  );
  const capeConcreteStructure = new THREE.InstancedMesh(
    pillarGeometry,
    capeConcreteMaterial,
    capeSupportFrames.length * 6,
  );
  const capeCrossbeams = new THREE.InstancedMesh(
    beamGeometry,
    capeSteelMaterial,
    capeSupportFrames.length,
  );
  const capeBraces = new THREE.InstancedMesh(
    braceGeometry,
    capeSteelMaterial,
    capeSupportFrames.length * CAPE_ASTER_SUPPORT_BRACING_CONTRACT.bracesPerSupport +
      CAPE_ASTER_SUPPORT_BRACING_CONTRACT.longitudinalBays *
        CAPE_ASTER_SUPPORT_BRACING_CONTRACT.longitudinalBracesPerBay +
      CAPE_ASTER_SUPPORT_BRACING_CONTRACT.underdeckBays *
        CAPE_ASTER_SUPPORT_BRACING_CONTRACT.underdeckBracesPerBay,
  );
  const capeGirders = configureMesh(
    new THREE.Mesh(capeGirderGeometry, capeSteelMaterial),
    true,
    true,
  );
  pillars.name = "Coastal viaduct twin limestone piers";
  beams.name = "Coastal viaduct limestone soffits";
  bearings.name = "Coastal viaduct banked bearings";
  capeConcreteStructure.name = "Cape Aster concrete piers caps and water footings";
  capeCrossbeams.name = "Cape Aster steel crossbeams";
  capeGirders.name = "Cape Aster longitudinal steel girders";
  capeBraces.name = "Cape Aster diagonal viaduct wind braces";
  const frame = track.sample(0);
  const dummy = new THREE.Object3D();
  const localUp = new THREE.Vector3(0, 1, 0);
  const braceStart = new THREE.Vector3();
  const braceEnd = new THREE.Vector3();
  const braceDirection = new THREE.Vector3();
  const ordinaryBearingSize = [2.5, 0.46, 2.6] as const;
  const capeBearingSize = [2.62, 0.46, 2.85] as const;
  for (let index = 0; index < supportFrames.length; index += 1) {
    const support = supportFrames[index];
    if (support === undefined) {
      continue;
    }
    track.sample(support.progress, 0, frame);
    const baseHeight = -2.4;
    for (let sideIndex = 0; sideIndex < 2; sideIndex += 1) {
      const lateral = sideIndex === 0 ? -4.2 : 4.2;
      const bearing = getBridgeBearingPlacement(
        frame,
        lateral,
        -0.52,
        0.72,
        ordinaryBearingSize,
      );
      const pillarHeight = Math.max(4, bearing.center.y - baseHeight);
      dummy.position.set(
        bearing.center.x,
        baseHeight + pillarHeight * 0.5,
        bearing.center.z,
      );
      dummy.quaternion.identity();
      dummy.scale.set(2.1, pillarHeight, 2.2);
      dummy.updateMatrix();
      pillars.setMatrixAt(index * 2 + sideIndex, dummy.matrix);
      bearings.setMatrixAt(index * 2 + sideIndex, bearing.matrix);
    }
    applyFrame(dummy, frame, 0, -0.52);
    dummy.scale.set(1, 1, 1);
    dummy.updateMatrix();
    beams.setMatrixAt(index, dummy.matrix);
  }

  for (let index = 0; index < capeSupportFrames.length; index += 1) {
    const support = capeSupportFrames[index];
    if (support === undefined) {
      continue;
    }
    track.sample(support.progress, 0, frame);
    const capeBearings = [
      getBridgeBearingPlacement(
        frame,
        -4.35,
        -2.32,
        0.72,
        capeBearingSize,
      ),
      getBridgeBearingPlacement(
        frame,
        4.35,
        -2.32,
        0.72,
        capeBearingSize,
      ),
    ] as const;
    const capeBaseHeight = -3.35;
    for (let sideIndex = 0; sideIndex < 2; sideIndex += 1) {
      const bearing = capeBearings[sideIndex];
      if (bearing === undefined) {
        continue;
      }
      const baseHeight = capeBaseHeight;
      const pillarTop = bearing.center.y;
      const pillarHeight = Math.max(1.2, pillarTop - baseHeight);
      dummy.position.set(
        bearing.center.x,
        baseHeight + pillarHeight * 0.5,
        bearing.center.z,
      );
      dummy.quaternion.identity();
      dummy.scale.set(1.72, pillarHeight, 1.82);
      dummy.updateMatrix();
      const instanceIndex = index * 6 + sideIndex * 3;
      capeConcreteStructure.setMatrixAt(instanceIndex, dummy.matrix);

      dummy.position.set(bearing.center.x, -3.02, bearing.center.z);
      dummy.scale.set(2.75, 0.66, 3.05);
      dummy.updateMatrix();
      capeConcreteStructure.setMatrixAt(instanceIndex + 1, dummy.matrix);

      capeConcreteStructure.setMatrixAt(instanceIndex + 2, bearing.matrix);
    }
    const bracePairs = [
      [capeBearings[0].center, capeBearings[1].center],
      [capeBearings[1].center, capeBearings[0].center],
    ] as const;
    bracePairs.forEach(([baseBearing, topBearing], braceIndex) => {
      braceStart.copy(baseBearing);
      braceStart.y = capeBaseHeight + 0.5;
      braceEnd.copy(topBearing);
      braceEnd.y -= 0.52;
      braceDirection.subVectors(braceEnd, braceStart);
      const braceLength = braceDirection.length();
      dummy.position.copy(braceStart).add(braceEnd).multiplyScalar(0.5);
      dummy.quaternion.setFromUnitVectors(localUp, braceDirection.normalize());
      dummy.scale.set(1, braceLength, 1);
      dummy.updateMatrix();
      capeBraces.setMatrixAt(index * 2 + braceIndex, dummy.matrix);
    });
    applyFrame(dummy, frame, 0, -2.32);
    dummy.scale.set(1, 1, 1);
    dummy.updateMatrix();
    capeCrossbeams.setMatrixAt(index, dummy.matrix);
  }

  const longitudinalBraceStart = new THREE.Vector3();
  const longitudinalBraceEnd = new THREE.Vector3();
  const longitudinalBraceDirection = new THREE.Vector3();
  let longitudinalBraceIndex =
    capeSupportFrames.length * CAPE_ASTER_SUPPORT_BRACING_CONTRACT.bracesPerSupport;
  for (
    let bayIndex = 0;
    bayIndex < CAPE_ASTER_SUPPORT_BRACING_CONTRACT.longitudinalBays;
    bayIndex += 1
  ) {
    const startSupport = capeSupportFrames[bayIndex];
    const endSupport = capeSupportFrames[bayIndex + 1];
    if (startSupport === undefined || endSupport === undefined) {
      continue;
    }
    const startFrame = track.sample(startSupport.progress);
    const endFrame = track.sample(endSupport.progress);
    for (const side of [-1, 1]) {
      const startLow = startFrame.center
        .clone()
        .addScaledVector(startFrame.right, side * 4.35)
        .addScaledVector(startFrame.normal, -2.12);
      const startHigh = startFrame.center
        .clone()
        .addScaledVector(startFrame.right, side * 4.35)
        .addScaledVector(startFrame.normal, -0.92);
      const endLow = endFrame.center
        .clone()
        .addScaledVector(endFrame.right, side * 4.35)
        .addScaledVector(endFrame.normal, -2.12);
      const endHigh = endFrame.center
        .clone()
        .addScaledVector(endFrame.right, side * 4.35)
        .addScaledVector(endFrame.normal, -0.92);
      for (const [from, to] of [
        [startLow, endHigh],
        [startHigh, endLow],
      ] as const) {
        longitudinalBraceStart.copy(from);
        longitudinalBraceEnd.copy(to);
        longitudinalBraceDirection.subVectors(
          longitudinalBraceEnd,
          longitudinalBraceStart,
        );
        const braceLength = longitudinalBraceDirection.length();
        dummy.position
          .copy(longitudinalBraceStart)
          .add(longitudinalBraceEnd)
          .multiplyScalar(0.5);
        dummy.quaternion.setFromUnitVectors(
          localUp,
          longitudinalBraceDirection.normalize(),
        );
        dummy.scale.set(1, braceLength, 1);
        dummy.updateMatrix();
        capeBraces.setMatrixAt(longitudinalBraceIndex, dummy.matrix);
        longitudinalBraceIndex += 1;
      }
    }
  }

  let underdeckBraceIndex =
    capeSupportFrames.length * CAPE_ASTER_SUPPORT_BRACING_CONTRACT.bracesPerSupport +
    CAPE_ASTER_SUPPORT_BRACING_CONTRACT.longitudinalBays *
      CAPE_ASTER_SUPPORT_BRACING_CONTRACT.longitudinalBracesPerBay;
  const underdeckStartProgress = 0.067;
  const underdeckEndProgress = 0.148;
  const underdeckBraceStart = new THREE.Vector3();
  const underdeckBraceEnd = new THREE.Vector3();
  const underdeckBraceDirection = new THREE.Vector3();
  for (
    let bayIndex = 0;
    bayIndex < CAPE_ASTER_SUPPORT_BRACING_CONTRACT.underdeckBays;
    bayIndex += 1
  ) {
    const progress = THREE.MathUtils.lerp(
      underdeckStartProgress,
      underdeckEndProgress,
      bayIndex /
        Math.max(1, CAPE_ASTER_SUPPORT_BRACING_CONTRACT.underdeckBays - 1),
    );
    const underdeckFrame = track.sample(progress);
    for (const side of [-1, 1]) {
      const outerLow = underdeckFrame.center
        .clone()
        .addScaledVector(underdeckFrame.right, side * 4.85)
        .addScaledVector(underdeckFrame.normal, -1.98);
      const outerHigh = underdeckFrame.center
        .clone()
        .addScaledVector(underdeckFrame.right, side * 4.85)
        .addScaledVector(underdeckFrame.normal, -0.92);
      const innerLow = underdeckFrame.center
        .clone()
        .addScaledVector(underdeckFrame.normal, -1.98);
      const innerHigh = underdeckFrame.center
        .clone()
        .addScaledVector(underdeckFrame.normal, -0.92);
      for (const [from, to] of [
        [outerLow, innerHigh],
        [outerHigh, innerLow],
      ] as const) {
        underdeckBraceStart.copy(from);
        underdeckBraceEnd.copy(to);
        underdeckBraceDirection.subVectors(
          underdeckBraceEnd,
          underdeckBraceStart,
        );
        const braceLength = underdeckBraceDirection.length();
        dummy.position
          .copy(underdeckBraceStart)
          .add(underdeckBraceEnd)
          .multiplyScalar(0.5);
        dummy.quaternion.setFromUnitVectors(
          localUp,
          underdeckBraceDirection.normalize(),
        );
        dummy.scale.set(1, braceLength, 1);
        dummy.updateMatrix();
        capeBraces.setMatrixAt(underdeckBraceIndex, dummy.matrix);
        underdeckBraceIndex += 1;
      }
    }
  }
  capeBraces.name = "Cape Aster anchored longitudinal truss braces";

  for (const supportMesh of [
    pillars,
    beams,
    bearings,
    capeConcreteStructure,
    capeCrossbeams,
    capeBraces,
  ]) {
    supportMesh.instanceMatrix.needsUpdate = true;
    supportMesh.castShadow = true;
    supportMesh.receiveShadow = true;
    supportMesh.computeBoundingSphere();
  }
  group.add(
    pillars,
    beams,
    bearings,
    capeConcreteStructure,
    capeCrossbeams,
    capeBraces,
    capeGirders,
  );
  return group;
};

const createTerrain = (
  track: SuntraceTrack,
  materials: SuntraceMaterialLibrary,
  geometries: THREE.BufferGeometry[],
  localMaterials: THREE.Material[],
): THREE.Group => {
  const group = new THREE.Group();
  group.name = "Karst island and sea";
  const islandGeometry = createIslandGeometry();
  const seaGeometry = new THREE.PlaneGeometry(4_200, 4_200, 160, 160);
  seaGeometry.rotateX(-Math.PI / 2);
  const waterlineShadowGeometry = createCapeWaterlineBandGeometry("wet-rock");
  geometries.push(
    islandGeometry,
    seaGeometry,
    waterlineShadowGeometry,
  );
  const island = configureMesh(
    new THREE.Mesh(islandGeometry, materials.terrain),
    true,
    true,
  );
  const sea = new THREE.Mesh(seaGeometry, materials.water);
  sea.position.y = -3.1;
  sea.receiveShadow = false;
  sea.renderOrder = -1;
  const waterlineShadowMaterial = new THREE.MeshBasicMaterial({
    color: 0x2f555b,
    transparent: true,
    opacity: 0.2,
    depthWrite: false,
  });
  localMaterials.push(waterlineShadowMaterial);
  const waterlineShadow = new THREE.Mesh(
    waterlineShadowGeometry,
    waterlineShadowMaterial,
  );
  waterlineShadow.name = "Cape Aster broken wet-rock waterline";
  waterlineShadow.renderOrder = 1;
  group.add(sea, island, waterlineShadow);

  const rockGeometries = Array.from(
    { length: KARST_ARCHETYPES.length },
    (_, index) => createKarstRockGeometry(index),
  );
  geometries.push(...rockGeometries);
  const rocks = rockGeometries.map(
    (geometry) => new THREE.InstancedMesh(geometry, materials.rock, 12),
  );
  const random = createRandom(0xbadc0ffe);
  const dummy = new THREE.Object3D();
  const capeKarstClearance = Array.from({ length: 17 }, (_, index) => {
    const progress = THREE.MathUtils.lerp(0.068, 0.18, index / 16);
    const frame = track.sample(progress);
    return frame.center.clone().addScaledVector(frame.right, -42);
  });
  const clusterAngles = [0.08, 0.58, 1.21, 1.86, 2.52, 3.16, 3.79, 4.45, 5.08, 5.72];
  const scaleRanges = [
    [4.5, 8.5, 14, 29, 4.5, 9],
    [7, 14, 8, 18, 5.5, 12],
    [5.5, 11, 11, 24, 6, 13],
    [9, 18, 6, 14, 8, 17],
    [4, 7.5, 16, 31, 4.5, 8.5],
    [6, 13, 10, 22, 5, 11],
  ] as const;
  for (let index = 0; index < 72; index += 1) {
    const variant = index % rockGeometries.length;
    const instanceIndex = Math.floor(index / rockGeometries.length);
    const clusterIndex = (index * 7 + Math.floor(index / 20)) % clusterAngles.length;
    const clusterAngle = clusterAngles[clusterIndex] ?? 0;
    const angle = clusterAngle + (random() - 0.5) * (0.12 + random() * 0.18);
    const radius = 380 + random() * 70;
    const x = Math.cos(angle) * radius * 1.07;
    const z = Math.sin(angle) * radius * 0.91;
    const range = scaleRanges[variant] ?? scaleRanges[0];
    const scaleX = range[0] + random() * (range[1] - range[0]);
    const scaleY = range[2] + random() * (range[3] - range[2]);
    const scaleZ = range[4] + random() * (range[5] - range[4]);
    const surfaceHeight = getSuntraceRenderableSurfaceHeight(x, z);
    dummy.position.set(x, surfaceHeight + scaleY * 0.42, z);
    dummy.rotation.set((random() - 0.5) * 0.18, random() * Math.PI, (random() - 0.5) * 0.14);
    const overlapsCapeChapter = capeKarstClearance.some(
      (anchor) => anchor.distanceToSquared(dummy.position) < 10_000,
    );
    const hidden =
      overlapsCapeChapter ||
      surfaceHeight < SUNTRACE_WORLD_PROP_GROUNDING_CONTRACT.minimumVisibleSurfaceHeight;
    dummy.scale.set(hidden ? 0.01 : scaleX, hidden ? 0.01 : scaleY, hidden ? 0.01 : scaleZ);
    dummy.updateMatrix();
    rocks[variant]?.setMatrixAt(instanceIndex, dummy.matrix);
  }
  for (const rockMesh of rocks) {
    rockMesh.instanceMatrix.needsUpdate = true;
    rockMesh.castShadow = true;
    rockMesh.receiveShadow = true;
  }
  group.add(...rocks);
  return group;
};

export const createTempestTunnel = (
  track: SuntraceTrack,
  materials: Pick<SuntraceMaterialLibrary, "limestone" | "limestoneDark">,
  geometries: THREE.BufferGeometry[],
  localMaterials: THREE.Material[],
): THREE.Group => {
  const group = new THREE.Group();
  group.name = "Tempest tunnel";
  const archGeometry = new THREE.TorusGeometry(8.35, 0.42, 8, 30, Math.PI);
  const wallGeometry = new THREE.BoxGeometry(0.72, 5.2, 11.2);
  const ceilingGeometry = new THREE.BoxGeometry(15.8, 0.48, 11.2);
  const lampGeometry = new THREE.CapsuleGeometry(0.08, 0.85, 3, 8);
  const lampMaterial = new THREE.MeshBasicMaterial({ color: 0xffd38c });
  const interiorMaterial = materials.limestoneDark.clone();
  interiorMaterial.name = "Tempest tunnel interior";
  interiorMaterial.emissive.setHex(0x3a2414);
  interiorMaterial.emissiveIntensity = 0.16;
  localMaterials.push(lampMaterial, interiorMaterial);
  geometries.push(archGeometry, wallGeometry, ceilingGeometry, lampGeometry);
  const frame = track.sample(0.58);

  for (let index = 0; index < 15; index += 1) {
    const progress = 0.575 + index * 0.0058;
    track.sample(progress, 0, frame);
    const arch = configureMesh(
      new THREE.Mesh(archGeometry, materials.limestone),
      true,
      true,
    );
    applyFrame(arch, frame, 0, 0.45);
    const leftWall = configureMesh(
      new THREE.Mesh(wallGeometry, interiorMaterial),
      true,
      true,
    );
    const rightWall = leftWall.clone();
    const ceiling = configureMesh(
      new THREE.Mesh(ceilingGeometry, interiorMaterial),
      true,
      true,
    );
    applyFrame(leftWall, frame, -8.05, 2.45);
    applyFrame(rightWall, frame, 8.05, 2.45);
    applyFrame(ceiling, frame, 0, 8.1);
    group.add(arch, leftWall, rightWall, ceiling);

    if (index % 3 === 1) {
      for (const side of [-1, 1]) {
        const lamp = new THREE.Mesh(lampGeometry, lampMaterial);
        applyFrame(lamp, frame, side * 5.9, 6.2);
        lamp.rotation.z += Math.PI / 2;
        group.add(lamp);
      }
    }
  }

  return group;
};

const createStartGantry = (
  track: SuntraceTrack,
  materials: SuntraceMaterialLibrary,
  geometries: THREE.BufferGeometry[],
  localMaterials: THREE.Material[],
  localTextures: THREE.Texture[],
): THREE.Group => {
  const group = new THREE.Group();
  group.name = "Solar trace gantry";
  const beamGeometry = new THREE.BoxGeometry(17.2, 0.34, 0.48);
  const towerGeometry = new THREE.BoxGeometry(0.4, 8.6, 0.52);
  const bladeGeometry = new THREE.BoxGeometry(2.15, 0.11, 0.07);
  const identityPanelGeometry = new THREE.BoxGeometry(
    SUNTRACE_START_GANTRY_IDENTITY_CONTRACT.panelWidth,
    SUNTRACE_START_GANTRY_IDENTITY_CONTRACT.panelHeight,
    SUNTRACE_START_GANTRY_IDENTITY_CONTRACT.panelDepth,
  );
  const identityMountGeometry = new THREE.BoxGeometry(
    0.18,
    SUNTRACE_START_GANTRY_IDENTITY_CONTRACT.mountHeight,
    0.2,
  );
  const identityFaceGeometry = new THREE.PlaneGeometry(8.08, 1.22);
  const identityTexture = createCapeAsterTexture();
  const identityMaterial = new THREE.MeshStandardMaterial({
    color: 0xffffff,
    map: identityTexture,
    emissive: 0x2c1a13,
    emissiveMap: identityTexture,
    emissiveIntensity: 0.16,
    roughness: 0.5,
    metalness: 0.02,
  });
  const glowingMaterial = new THREE.MeshBasicMaterial({
    color: 0xffc66d,
    toneMapped: false,
  });
  localMaterials.push(glowingMaterial);
  geometries.push(
    beamGeometry,
    towerGeometry,
    bladeGeometry,
    identityPanelGeometry,
    identityMountGeometry,
    identityFaceGeometry,
  );
  localMaterials.push(identityMaterial);
  localTextures.push(identityTexture);
  const frame = track.sample(0.039);
  const root = new THREE.Group();
  applyFrame(root, frame, 0, 0);
  const beam = configureMesh(
    new THREE.Mesh(beamGeometry, materials.guardrail),
    true,
    true,
  );
  beam.position.y = 8.35;
  root.add(beam);

  for (const side of [-1, 1]) {
    const tower = configureMesh(
      new THREE.Mesh(towerGeometry, materials.guardrail),
      true,
      true,
    );
    tower.position.set(side * 8.2, 4.15, 0);
    root.add(tower);
  }

  const identityPanel = configureMesh(
    new THREE.Mesh(identityPanelGeometry, materials.guardrail),
    true,
    true,
  );
  identityPanel.name = "SUNTRACE start gantry identity panel";
  identityPanel.position.set(
    0,
    SUNTRACE_START_GANTRY_IDENTITY_CONTRACT.panelCenterHeight,
    -0.3,
  );
  root.add(identityPanel);

  const identityFace = new THREE.Mesh(identityFaceGeometry, identityMaterial);
  identityFace.name = "SUNTRACE start gantry identity lettering";
  identityFace.position.set(
    0,
    SUNTRACE_START_GANTRY_IDENTITY_CONTRACT.panelCenterHeight,
    -0.4,
  );
  identityFace.rotation.y = Math.PI;
  root.add(identityFace);

  const accentDummy = new THREE.Object3D();

  const identityMounts = new THREE.InstancedMesh(
    identityMountGeometry,
    materials.guardrail,
    SUNTRACE_START_GANTRY_IDENTITY_CONTRACT.mountCount,
  );
  identityMounts.name = "SUNTRACE start gantry identity mounts";
  for (const [index, lateral] of [-3.2, 3.2].entries()) {
    accentDummy.position.set(lateral, 8.06, -0.3);
    accentDummy.updateMatrix();
    identityMounts.setMatrixAt(index, accentDummy.matrix);
  }
  identityMounts.instanceMatrix.needsUpdate = true;
  identityMounts.castShadow = true;
  identityMounts.receiveShadow = true;
  root.add(identityMounts);

  for (let index = 0; index < 6; index += 1) {
    const blade = new THREE.Mesh(bladeGeometry, glowingMaterial);
    blade.position.set(-6.25 + index * 2.5, 8.34, -0.3);
    blade.rotation.z = index % 2 === 0 ? 0.23 : -0.23;
    root.add(blade);
  }
  group.add(root);
  return group;
};

const createLighthouse = (
  track: SuntraceTrack,
  materials: SuntraceMaterialLibrary,
  geometries: THREE.BufferGeometry[],
  localMaterials: THREE.Material[],
): THREE.Group => {
  const group = new THREE.Group();
  group.name = "Cape Aster lighthouse";
  const towerGeometry = new THREE.CylinderGeometry(3.6, 5.3, 31, 18, 3);
  const balconyGeometry = new THREE.CylinderGeometry(5.1, 5.1, 0.8, 20);
  const lanternGeometry = new THREE.CylinderGeometry(2.5, 2.5, 4.4, 16);
  const roofGeometry = new THREE.ConeGeometry(3.1, 2.8, 16);
  const stripeGeometry = new THREE.CylinderGeometry(4.25, 4.45, 3.1, 18);
  geometries.push(
    towerGeometry,
    balconyGeometry,
    lanternGeometry,
    roofGeometry,
    stripeGeometry,
  );
  const glassMaterial = new THREE.MeshPhysicalMaterial({
    color: 0xffd79d,
    roughness: 0.08,
    metalness: 0.05,
    transmission: 0.58,
    thickness: 0.35,
    emissive: 0xff9f48,
    emissiveIntensity: 2.2,
  });
  localMaterials.push(glassMaterial);
  const frame = track.sample(0.295);
  group.position
    .copy(frame.center)
    .addScaledVector(frame.right, 90)
    .setY(2.4);
  const plinth = configureMesh(
    new THREE.Mesh(new THREE.CylinderGeometry(10, 13.5, 7, 9), materials.rock),
    true,
    true,
  );
  geometries.push(plinth.geometry);
  plinth.position.y = 3.5;
  const tower = configureMesh(
    new THREE.Mesh(towerGeometry, materials.limestone),
    true,
    true,
  );
  tower.position.y = 22.4;
  const stripe = new THREE.Mesh(stripeGeometry, materials.safety);
  stripe.position.y = 19.2;
  const balcony = configureMesh(
    new THREE.Mesh(balconyGeometry, materials.guardrail),
    true,
    true,
  );
  balcony.position.y = 38;
  const lantern = new THREE.Mesh(lanternGeometry, glassMaterial);
  lantern.position.y = 40.4;
  const roof = configureMesh(
    new THREE.Mesh(roofGeometry, materials.safety),
    true,
    false,
  );
  roof.position.y = 44;
  const beacon = new THREE.PointLight(0xffc66d, 620, 100, 2);
  beacon.position.y = 40.5;
  group.add(plinth, tower, stripe, balcony, lantern, roof, beacon);
  return group;
};

const createMarshalPost = (
  track: SuntraceTrack,
  materials: SuntraceMaterialLibrary,
  geometries: THREE.BufferGeometry[],
  localMaterials: THREE.Material[],
  localTextures: THREE.Texture[],
): THREE.Group => {
  const group = new THREE.Group();
  group.name = "Aurelia Sector 01 marshal post";
  const root = new THREE.Group();
  const frame = track.sample(0.405);
  applyFrame(root, frame, track.width * 0.5 + 7.1, 0.04);
  root.scale.setScalar(1.28);

  const platformGeometry = new THREE.BoxGeometry(7.2, 0.36, 4.6);
  const supportGeometry = new THREE.BoxGeometry(0.34, 4.8, 0.34);
  const cabinGeometry = new THREE.BoxGeometry(5.7, 2.8, 3.45);
  const windowGeometry = new THREE.PlaneGeometry(3.8, 1.45);
  const sideWindowGeometry = new THREE.PlaneGeometry(2.1, 1.3);
  const roofGeometry = new THREE.BoxGeometry(6.45, 0.28, 4.18);
  const awningGeometry = new THREE.BoxGeometry(4.7, 0.2, 1.05);
  const signBackGeometry = new THREE.BoxGeometry(5.2, 1.34, 0.14);
  const signFaceGeometry = new THREE.PlaneGeometry(5.04, 1.22);
  const mastGeometry = new THREE.CylinderGeometry(0.055, 0.07, 5.2, 8);
  const flagGeometry = new THREE.PlaneGeometry(1.45, 0.86);
  const railGeometry = new THREE.BoxGeometry(0.1, 0.1, 3.9);
  const stairGeometry = new THREE.BoxGeometry(2.2, 0.24, 0.64);
  const footingGeometry = new THREE.BoxGeometry(7.8, 0.56, 5.05);
  const walkwayGeometry = new THREE.BoxGeometry(2.8, 0.18, 1.5);
  const ladderRailGeometry = new THREE.BoxGeometry(0.08, 3.5, 0.08);
  const ladderRungGeometry = new THREE.BoxGeometry(0.72, 0.06, 0.08);
  const conduitGeometry = new THREE.CylinderGeometry(0.055, 0.055, 2.5, 8);
  const utilityBoxGeometry = new THREE.BoxGeometry(0.7, 0.9, 0.24);
  const signBoltGeometry = new THREE.CylinderGeometry(0.06, 0.06, 0.08, 10);
  const marshalTorsoGeometry = new THREE.BoxGeometry(0.34, 0.72, 0.24);
  const marshalHeadGeometry = new THREE.SphereGeometry(0.14, 10, 8);
  const marshalLegGeometry = new THREE.BoxGeometry(0.1, 0.64, 0.11);
  geometries.push(
    platformGeometry,
    supportGeometry,
    cabinGeometry,
    windowGeometry,
    sideWindowGeometry,
    roofGeometry,
    awningGeometry,
    signBackGeometry,
    signFaceGeometry,
    mastGeometry,
    flagGeometry,
    railGeometry,
    stairGeometry,
    footingGeometry,
    walkwayGeometry,
    ladderRailGeometry,
    ladderRungGeometry,
    conduitGeometry,
    utilityBoxGeometry,
    signBoltGeometry,
    marshalTorsoGeometry,
    marshalHeadGeometry,
    marshalLegGeometry,
  );

  const glassMaterial = new THREE.MeshPhysicalMaterial({
    color: 0x647b84,
    roughness: 0.16,
    metalness: 0.03,
    transmission: 0.46,
    thickness: 0.22,
    envMapIntensity: 0.8,
  });
  const roadBookTexture = createRoadBookTexture();
  const signMaterial = new THREE.MeshStandardMaterial({
    map: roadBookTexture,
    color: 0xffffff,
    roughness: 0.68,
    metalness: 0,
  });
  const flagMaterial = new THREE.MeshStandardMaterial({
    color: 0xb74732,
    roughness: 0.82,
    metalness: 0,
    side: THREE.DoubleSide,
  });
  localMaterials.push(glassMaterial, signMaterial, flagMaterial);
  localTextures.push(roadBookTexture);

  const footing = configureMesh(
    new THREE.Mesh(footingGeometry, materials.limestoneDark),
    true,
    true,
  );
  footing.position.y = -0.2;
  root.add(footing);

  const platform = configureMesh(
    new THREE.Mesh(platformGeometry, materials.limestoneDark),
    true,
    true,
  );
  platform.position.y = 0.25;
  root.add(platform);

  for (const x of [-2.7, 2.7]) {
    for (const z of [-1.65, 1.65]) {
      const support = configureMesh(
        new THREE.Mesh(supportGeometry, materials.limestoneDark),
        true,
        true,
      );
      support.position.set(x, -2.25, z);
      root.add(support);
    }
  }

  const cabin = configureMesh(
    new THREE.Mesh(cabinGeometry, materials.limestone),
    true,
    true,
  );
  cabin.position.y = 1.88;
  root.add(cabin);

  const frontWindow = new THREE.Mesh(windowGeometry, glassMaterial);
  frontWindow.position.set(0, 2.15, -1.731);
  frontWindow.rotation.y = Math.PI;
  root.add(frontWindow);
  for (const side of [-1, 1]) {
    const sideWindow = new THREE.Mesh(sideWindowGeometry, glassMaterial);
    sideWindow.position.set(side * 2.856, 2.13, 0.15);
    sideWindow.rotation.y = side * Math.PI * 0.5;
    root.add(sideWindow);
  }

  const roof = configureMesh(
    new THREE.Mesh(roofGeometry, materials.safety),
    true,
    true,
  );
  roof.position.y = 3.43;
  root.add(roof);
  const awning = configureMesh(
    new THREE.Mesh(awningGeometry, materials.guardrail),
    true,
    true,
  );
  awning.position.set(0, 3.16, -2.04);
  awning.rotation.x = -0.12;
  root.add(awning);

  const signBack = configureMesh(
    new THREE.Mesh(signBackGeometry, materials.limestoneDark),
    true,
    true,
  );
  signBack.position.set(0, 4.92, -0.55);
  root.add(signBack);
  const signFace = new THREE.Mesh(signFaceGeometry, signMaterial);
  signFace.position.set(0, 4.92, -0.626);
  signFace.rotation.y = Math.PI;
  root.add(signFace);

  const mast = configureMesh(
    new THREE.Mesh(mastGeometry, materials.guardrail),
    true,
    true,
  );
  mast.position.set(3.18, 4.05, 1.35);
  root.add(mast);
  const flag = new THREE.Mesh(flagGeometry, flagMaterial);
  flag.position.set(3.9, 5.98, 1.35);
  root.add(flag);

  const walkway = configureMesh(
    new THREE.Mesh(walkwayGeometry, materials.limestoneDark),
    true,
    true,
  );
  walkway.position.set(-4.9, 0.34, 0.25);
  root.add(walkway);

  for (const x of [2.98, 3.62]) {
    const ladderRail = configureMesh(
      new THREE.Mesh(ladderRailGeometry, materials.guardrail),
      true,
      true,
    );
    ladderRail.position.set(x, 1.82, 1.78);
    root.add(ladderRail);
  }
  for (let rung = 0; rung < 9; rung += 1) {
    const ladderRung = configureMesh(
      new THREE.Mesh(ladderRungGeometry, materials.guardrail),
      true,
      true,
    );
    ladderRung.position.set(3.3, 0.35 + rung * 0.4, 1.78);
    root.add(ladderRung);
  }

  const conduit = configureMesh(
    new THREE.Mesh(conduitGeometry, materials.guardrail),
    true,
    true,
  );
  conduit.position.set(2.96, 1.72, 1.5);
  root.add(conduit);
  const utilityBox = configureMesh(
    new THREE.Mesh(utilityBoxGeometry, materials.limestoneDark),
    true,
    true,
  );
  utilityBox.position.set(2.98, 1.04, 1.5);
  root.add(utilityBox);

  for (const x of [-2.38, 2.38]) {
    for (const y of [4.4, 5.44]) {
      const signBolt = configureMesh(
        new THREE.Mesh(signBoltGeometry, materials.guardrail),
        true,
        true,
      );
      signBolt.position.set(x, y, -0.71);
      signBolt.rotation.x = Math.PI * 0.5;
      root.add(signBolt);
    }
  }

  for (let marshalIndex = 0; marshalIndex < 2; marshalIndex += 1) {
    const marshal = new THREE.Group();
    marshal.position.set(marshalIndex === 0 ? -2.2 : 2.1, 0.68, -2.03);
    marshal.rotation.y = marshalIndex === 0 ? -0.18 : 0.22;
    const torso = configureMesh(
      new THREE.Mesh(marshalTorsoGeometry, marshalIndex === 0 ? materials.safety : materials.limestoneDark),
      true,
      true,
    );
    torso.position.y = 0.68;
    const head = configureMesh(
      new THREE.Mesh(marshalHeadGeometry, materials.limestone),
      true,
      true,
    );
    head.position.y = 1.2;
    marshal.add(torso, head);
    for (const side of [-1, 1]) {
      const leg = configureMesh(
        new THREE.Mesh(marshalLegGeometry, materials.limestoneDark),
        true,
        true,
      );
      leg.position.set(side * 0.1, 0.07, 0);
      marshal.add(leg);
    }
    root.add(marshal);
  }

  for (const side of [-1, 1]) {
    const rail = configureMesh(
      new THREE.Mesh(railGeometry, materials.guardrail),
      true,
      true,
    );
    rail.position.set(side * 3.25, 1.02, 0);
    root.add(rail);
  }

  for (let step = 0; step < 5; step += 1) {
    const stair = configureMesh(
      new THREE.Mesh(stairGeometry, materials.limestoneDark),
      true,
      true,
    );
    stair.position.set(-2.25, -0.04 - step * 0.24, 2.55 + step * 0.52);
    root.add(stair);
  }

  group.add(root);
  return group;
};

const createVillage = (
  track: SuntraceTrack,
  materials: SuntraceMaterialLibrary,
  geometries: THREE.BufferGeometry[],
  localMaterials: THREE.Material[],
): THREE.Group => {
  const group = new THREE.Group();
  group.name = "Limestone village";
  const houseGeometry = new THREE.BoxGeometry(1, 1, 1);
  const domeGeometry = new THREE.SphereGeometry(0.55, 16, 8, 0, Math.PI * 2, 0, Math.PI / 2);
  const roofGeometry = new THREE.BoxGeometry(1, 1, 1);
  const awningGeometry = new THREE.BoxGeometry(1, 0.12, 0.62);
  const windowGeometry = new THREE.PlaneGeometry(0.62, 1.1);
  const windowMaterial = new THREE.MeshStandardMaterial({
    color: 0x5e7680,
    roughness: 0.18,
    metalness: 0.04,
    emissive: 0x6d4a2e,
    emissiveIntensity: 0.16,
  });
  const roofMaterial = new THREE.MeshStandardMaterial({
    color: 0x7d4b3b,
    roughness: 0.91,
    metalness: 0,
  });
  localMaterials.push(windowMaterial, roofMaterial);
  geometries.push(houseGeometry, domeGeometry, roofGeometry, awningGeometry, windowGeometry);
  const random = createRandom(0xaffe1023);
  const frame = track.sample(0.42);
  const candidate = new THREE.Vector3();

  for (let index = 0; index < 20; index += 1) {
    const root = new THREE.Group();
    let bestSurface = Number.NEGATIVE_INFINITY;
    let hasGroundedPosition = false;
    for (let attempt = 0; attempt < 10; attempt += 1) {
      track.sample(0.38 + random() * 0.11, 0, frame);
      const side = attempt % 2 === 0 ? 1 : -1;
      const lateral = side * (24 + random() * 62);
      candidate.copy(frame.center).addScaledVector(frame.right, lateral);
      const surface = getSuntraceRenderableSurfaceHeight(candidate.x, candidate.z);
      if (
        surface >= SUNTRACE_WORLD_PROP_GROUNDING_CONTRACT.minimumVisibleSurfaceHeight &&
        surface > bestSurface
      ) {
        bestSurface = surface;
        root.position.copy(candidate).setY(surface);
        hasGroundedPosition = true;
      }
    }
    if (!hasGroundedPosition) {
      continue;
    }
    root.rotation.y = Math.round(random() * 3) * Math.PI * 0.5 + (random() - 0.5) * 0.08;
    const width = 4.8 + random() * 4.2;
    const depth = 4.6 + random() * 4.4;
    const height = 4.2 + random() * 4.6;
    const house = configureMesh(
      new THREE.Mesh(houseGeometry, materials.limestone),
      true,
      true,
    );
    house.scale.set(width, height, depth);
    house.position.y = height * 0.5;
    root.add(house);

    const roof = configureMesh(
      new THREE.Mesh(roofGeometry, roofMaterial),
      true,
      true,
    );
    roof.scale.set(width + 0.44, 0.28, depth + 0.44);
    roof.position.y = height + 0.14;
    root.add(roof);

    if (index % 4 === 0) {
      const dome = configureMesh(
        new THREE.Mesh(domeGeometry, materials.limestone),
        true,
        true,
      );
      dome.scale.set(width * 0.45, width * 0.34, width * 0.45);
      dome.position.y = height;
      root.add(dome);
    }

    const windowCount = 2 + (index % 3);
    for (let windowIndex = 0; windowIndex < windowCount; windowIndex += 1) {
      const window = new THREE.Mesh(
        windowGeometry,
        windowMaterial,
      );
      window.position.set(
        -width * 0.3 + windowIndex * (width * 0.6) / Math.max(1, windowCount - 1),
        height * 0.56,
        depth * 0.501,
      );
      const awning = configureMesh(
        new THREE.Mesh(awningGeometry, index % 2 === 0 ? roofMaterial : materials.safety),
        true,
        true,
      );
      awning.scale.set(1.05, 1, 1);
      awning.position.set(window.position.x, window.position.y + 0.82, depth * 0.55);
      root.add(awning);
      root.add(window);
    }
    group.add(root);
  }
  return group;
};

const createAqueduct = (
  materials: SuntraceMaterialLibrary,
  geometries: THREE.BufferGeometry[],
): THREE.Group => {
  const group = new THREE.Group();
  group.name = "Helios aqueduct";
  group.position.set(-112, 30, -39);
  group.rotation.y = -0.26;
  const pillarGeometry = new THREE.BoxGeometry(3.1, 22, 4.8);
  const beamGeometry = new THREE.BoxGeometry(16.2, 3.2, 4.8);
  const archGeometry = new THREE.TorusGeometry(6.4, 1.55, 8, 22, Math.PI);
  geometries.push(pillarGeometry, beamGeometry, archGeometry);

  for (let index = 0; index < 7; index += 1) {
    const x = (index - 3) * 13.8;
    const pillar = configureMesh(
      new THREE.Mesh(pillarGeometry, materials.limestone),
      true,
      true,
    );
    pillar.position.set(x - 6.9, 11, 0);
    const arch = configureMesh(
      new THREE.Mesh(archGeometry, materials.limestone),
      true,
      true,
    );
    arch.position.set(x, 13, 0);
    const beam = configureMesh(
      new THREE.Mesh(beamGeometry, materials.limestone),
      true,
      true,
    );
    beam.position.set(x, 23.4, 0);
    group.add(pillar, arch, beam);
  }
  return group;
};

const createHeliosSpire = (
  materials: SuntraceMaterialLibrary,
  geometries: THREE.BufferGeometry[],
  localMaterials: THREE.Material[],
): THREE.Group => {
  const group = new THREE.Group();
  group.name = "Helios storm observatory";
  group.position.set(0, getIslandSurfaceHeight(0, 0) - 1.5, 0);
  const foundationGeometry = new THREE.CylinderGeometry(12.5, 15.5, 7, 12);
  const towerGeometry = new THREE.CylinderGeometry(3.2, 8.5, 64, 24, 5);
  const crownGeometry = new THREE.TorusGeometry(7.8, 0.28, 8, 48);
  const beaconGeometry = new THREE.IcosahedronGeometry(1.5, 2);
  const finGeometry = new THREE.BoxGeometry(0.24, 18, 1.1);
  geometries.push(
    foundationGeometry,
    towerGeometry,
    crownGeometry,
    beaconGeometry,
    finGeometry,
  );
  const beaconMaterial = new THREE.MeshBasicMaterial({
    color: 0xffd38f,
    toneMapped: false,
  });
  localMaterials.push(beaconMaterial);
  const foundation = configureMesh(
    new THREE.Mesh(foundationGeometry, materials.limestoneDark),
    true,
    true,
  );
  foundation.position.y = 3.5;
  const tower = configureMesh(
    new THREE.Mesh(towerGeometry, materials.limestone),
    true,
    true,
  );
  tower.position.y = 38.5;
  group.add(foundation, tower);
  for (let index = 0; index < 4; index += 1) {
    const angle = index * Math.PI * 0.5;
    const fin = configureMesh(
      new THREE.Mesh(finGeometry, materials.safety),
      true,
      true,
    );
    fin.position.set(Math.sin(angle) * 4.3, 42, Math.cos(angle) * 4.3);
    fin.rotation.y = angle;
    group.add(fin);
  }
  const crownA = new THREE.Mesh(crownGeometry, materials.ribbon);
  crownA.position.y = 72;
  const crownB = crownA.clone();
  crownB.rotation.y = Math.PI / 2;
  const beacon = new THREE.Mesh(beaconGeometry, beaconMaterial);
  beacon.position.y = 72;
  const beaconLight = new THREE.PointLight(0xffc66d, 780, 120, 2);
  beaconLight.position.y = 72;
  group.add(crownA, crownB, beacon, beaconLight);
  return group;
};

type CypressCrownArchetype = {
  profile: readonly number[];
  height: number;
  width: number;
  depth: number;
  lean: number;
  phase: number;
};

const CYPRESS_CROWN_ARCHETYPES = [
  {
    profile: [0.72, 0.7, 0.67, 0.63, 0.58, 0.53, 0.47, 0.4, 0.33, 0.25, 0.17, 0.09, 0.015],
    height: 7.5,
    width: 1.05,
    depth: 0.92,
    lean: 0.08,
    phase: 0.4,
  },
  {
    profile: [0.84, 0.81, 0.77, 0.72, 0.66, 0.6, 0.53, 0.46, 0.38, 0.3, 0.22, 0.12, 0.018],
    height: 6.25,
    width: 1.34,
    depth: 1.02,
    lean: 0.46,
    phase: 2.1,
  },
  {
    profile: [0.64, 0.63, 0.61, 0.58, 0.54, 0.49, 0.44, 0.38, 0.31, 0.24, 0.17, 0.09, 0.014],
    height: 6.85,
    width: 1.18,
    depth: 1.3,
    lean: -0.3,
    phase: 4.3,
  },
] as const satisfies readonly CypressCrownArchetype[];

export const CAPE_ASTER_CYPRESS_CROWN_CONTRACT = Object.freeze({
  radialSegments: 18,
  vertexColors: true,
});

const CAPE_GROVE_PLACEMENTS = [
  { progress: 0.074, lateral: -21.5, scale: 0.94, yaw: 2.72, sway: 0.06 },
  { progress: 0.084, lateral: -18.2, scale: 0.88, yaw: 0.24, sway: -0.05 },
  { progress: 0.091, lateral: -26.4, scale: 1.12, yaw: 1.18, sway: 0.04 },
  { progress: 0.099, lateral: -21.3, scale: 0.76, yaw: 2.41, sway: -0.08 },
  { progress: 0.106, lateral: -32.1, scale: 1.2, yaw: 0.68, sway: 0.06 },
  { progress: 0.114, lateral: -24.2, scale: 0.96, yaw: 1.83, sway: -0.03 },
  { progress: 0.123, lateral: -28.7, scale: 0.82, yaw: 2.82, sway: 0.08 },
  { progress: 0.132, lateral: -18.9, scale: 1.08, yaw: 0.42, sway: -0.06 },
  { progress: 0.14, lateral: -33.4, scale: 0.9, yaw: 2.14, sway: 0.04 },
  { progress: 0.148, lateral: -25.1, scale: 1.16, yaw: 1.02, sway: -0.07 },
  { progress: 0.155, lateral: -19.7, scale: 0.8, yaw: 2.6, sway: 0.05 },
  { progress: 0.162, lateral: -30.5, scale: 1.02, yaw: 0.13, sway: -0.04 },
  { progress: 0.174, lateral: -17.8, scale: 1.1, yaw: 1.48, sway: 0.07 },
  { progress: 0.185, lateral: -25.6, scale: 0.84, yaw: 2.34, sway: -0.06 },
  { progress: 0.191, lateral: -20.2, scale: 0.98, yaw: 0.76, sway: 0.04 },
] as const;

const createCypressCrownGeometry = (
  archetype: CypressCrownArchetype,
): THREE.BufferGeometry => {
  const radialSegments = CAPE_ASTER_CYPRESS_CROWN_CONTRACT.radialSegments;
  const rings = archetype.profile.length - 1;
  const positions: number[] = [];
  const uvs: number[] = [];
  const colors: number[] = [];
  const indices: number[] = [];
  const lowerColor = new THREE.Color(0x1b2d25);
  const upperColor = new THREE.Color(0x4a6848);
  const foliageColor = new THREE.Color();

  for (let ring = 0; ring <= rings; ring += 1) {
    const vertical = ring / rings;
    const radius = archetype.profile[ring] ?? 0.1;
    const centerX = archetype.lean * Math.pow(vertical, 1.55);
    for (let segment = 0; segment <= radialSegments; segment += 1) {
      const angle = segment / radialSegments * Math.PI * 2;
      const asymmetry =
        1 +
        Math.sin(angle * 3 + archetype.phase) * 0.075 +
        Math.sin(angle * 5 - archetype.phase) * 0.035;
      positions.push(
        centerX + Math.cos(angle) * radius * asymmetry * archetype.width,
        (vertical - 0.5) * archetype.height,
        Math.sin(angle) * radius * asymmetry * archetype.depth,
      );
      uvs.push(segment / radialSegments, vertical);
      const tonalVariation = 0.92 +
        Math.sin(angle * 2.6 + archetype.phase) * 0.04 +
        Math.sin(vertical * 8.4 - archetype.phase) * 0.025;
      foliageColor
        .copy(lowerColor)
        .lerp(upperColor, vertical * 0.72)
        .multiplyScalar(tonalVariation);
      colors.push(foliageColor.r, foliageColor.g, foliageColor.b);
    }
  }

  const verticesPerRing = radialSegments + 1;
  for (let ring = 0; ring < rings; ring += 1) {
    for (let segment = 0; segment < radialSegments; segment += 1) {
      const current = ring * verticesPerRing + segment;
      const next = current + verticesPerRing;
      indices.push(current, next, current + 1, next, next + 1, current + 1);
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
  geometry.setAttribute("color", new THREE.Float32BufferAttribute(colors, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  geometry.computeBoundingSphere();
  return geometry;
};

const createVegetation = (
  track: SuntraceTrack,
  materials: SuntraceMaterialLibrary,
  geometries: THREE.BufferGeometry[],
  localMaterials: THREE.Material[],
): THREE.Group => {
  const group = new THREE.Group();
  group.name = "Cypress windbreaks and Cape grove";
  const trunkGeometry = new THREE.CylinderGeometry(0.13, 0.2, 2.4, 6);
  const crownGeometries = CYPRESS_CROWN_ARCHETYPES.map(
    (archetype) => createCypressCrownGeometry(archetype),
  );
  const undergrowthGeometry = new THREE.DodecahedronGeometry(0.72, 0);
  const stoneGeometries = CAPE_STONE_ARCHETYPES.map((archetype) =>
    createCapeStoneGeometry(archetype),
  );
  const screeApronGeometry = createCapeScreeApronGeometry(track);
  geometries.push(
    trunkGeometry,
    ...crownGeometries,
    undergrowthGeometry,
    ...stoneGeometries,
    screeApronGeometry,
  );
  const stoneMaterial = new THREE.MeshStandardMaterial({
    color: 0xfff2dc,
    emissive: 0x8b6245,
    emissiveIntensity: 0.3,
    roughness: 0.94,
    metalness: 0,
    envMapIntensity: 0.78,
    flatShading: true,
    vertexColors: true,
  });
  localMaterials.push(stoneMaterial);
  const trunkMaterial = materials.limestoneDark;
  const cypressMaterial = new THREE.MeshStandardMaterial({
    color: 0xffffff,
    roughness: materials.vegetation.roughness,
    metalness: materials.vegetation.metalness,
    envMapIntensity: materials.vegetation.envMapIntensity,
    vertexColors: CAPE_ASTER_CYPRESS_CROWN_CONTRACT.vertexColors,
    flatShading: false,
  });
  localMaterials.push(cypressMaterial);
  const vegetationCount = 96;
  const trunks = new THREE.InstancedMesh(trunkGeometry, trunkMaterial, vegetationCount);
  const crowns = crownGeometries.map(
    (geometry, index) => {
      const crown = new THREE.InstancedMesh(
        geometry,
        cypressMaterial,
        vegetationCount / crownGeometries.length,
      );
      crown.name = `Cypress crown archetype ${String(index + 1)}`;
      return crown;
    },
  );
  const random = createRandom(0x11235813);
  const frame = track.sample(0);
  const dummy = new THREE.Object3D();
  const base = new THREE.Vector3();
  const capeGroveCount = CAPE_GROVE_PLACEMENTS.length;

  for (let index = 0; index < vegetationCount; index += 1) {
    const isCapeGrove = index < capeGroveCount;
    const capePlacement = CAPE_GROVE_PLACEMENTS[index];
    const distributedIndex = index - capeGroveCount;
    const distributedCount = vegetationCount - capeGroveCount;
    const distributedDistance =
      ((distributedIndex + 0.42) / distributedCount) * 0.888;
    const distributedProgress = distributedDistance < 0.068
      ? distributedDistance
      : distributedDistance + 0.112;
    const progress = isCapeGrove
      ? capePlacement?.progress ?? 0.084
      : distributedProgress + random() * 0.0024;
    track.sample(progress, 0, frame);
    const side = isCapeGrove ? -1 : index % 3 === 0 ? -1 : 1;
    const lateral = isCapeGrove
      ? capePlacement?.lateral ?? -18
      : side * (18 + random() * 42);
    base.copy(frame.center).addScaledVector(frame.right, lateral);
    const surfaceHeight = isCapeGrove
      ? getCapeTerraceSurfaceHeight(frame, lateral) + 0.05
      : getSuntraceRenderableSurfaceHeight(base.x, base.z);
    base.setY(surfaceHeight);
    const scale = isCapeGrove
      ? capePlacement?.scale ?? 1
      : 0.65 + random() * 0.65;
    const visibleScale = isCapeGrove || surfaceHeight > 1.5 ? scale : 0.01;
    const trunkHeight = 2.4 * scale;
    dummy.position.set(base.x, base.y + trunkHeight * 0.5, base.z);
    dummy.rotation.set(0, isCapeGrove ? capePlacement?.yaw ?? 0 : random() * Math.PI, 0);
    dummy.scale.set(visibleScale, visibleScale, visibleScale);
    dummy.updateMatrix();
    trunks.setMatrixAt(index, dummy.matrix);
    const crownIndex = index % crownGeometries.length;
    const crownArchetype = CYPRESS_CROWN_ARCHETYPES[crownIndex] ?? CYPRESS_CROWN_ARCHETYPES[0];
    const visibleTrunkHeight = isCapeGrove
      ? THREE.MathUtils.clamp(0.96 + (scale - 1) * 0.68, 0.8, 1.2)
      : Math.max(0.9, 2.05 * scale);
    dummy.position.set(
      base.x,
      base.y + visibleTrunkHeight + crownArchetype.height * scale * 0.5,
      base.z,
    );
    dummy.rotation.z = isCapeGrove
      ? capePlacement?.sway ?? 0
      : (random() - 0.5) * 0.09;
    dummy.scale.set(visibleScale, visibleScale, visibleScale);
    dummy.updateMatrix();
    crowns[crownIndex]?.setMatrixAt(Math.floor(index / crownGeometries.length), dummy.matrix);
  }
  trunks.instanceMatrix.needsUpdate = true;
  trunks.castShadow = true;
  trunks.receiveShadow = true;
  for (const crown of crowns) {
    crown.instanceMatrix.needsUpdate = true;
    crown.castShadow = true;
    crown.receiveShadow = true;
    crown.computeBoundingSphere();
  }

  const contactUndergrowthCount = capeGroveCount * 2;
  const undergrowthCount = contactUndergrowthCount + 26;
  const undergrowth = new THREE.InstancedMesh(
    undergrowthGeometry,
    materials.vegetation,
    undergrowthCount,
  );
  undergrowth.name = "Cape Aster saltwind undergrowth";
  for (let index = 0; index < undergrowthCount; index += 1) {
    const contactsTrunk = index < contactUndergrowthCount;
    const capePlacement = contactsTrunk
      ? CAPE_GROVE_PLACEMENTS[Math.floor(index / 2)]
      : undefined;
    const side = index % 2 === 0 ? -1 : 1;
    const progress = capePlacement === undefined
      ? 0.084 + random() * 0.079
      : capePlacement.progress + side * (0.00065 + random() * 0.0004);
    track.sample(progress, 0, frame);
    const lateral = capePlacement === undefined
      ? -(14.2 + random() * 18.4)
      : capePlacement.lateral + side * (0.36 + random() * 0.42);
    base.copy(frame.center).addScaledVector(frame.right, lateral);
    const surfaceHeight = getCapeTerraceSurfaceHeight(frame, lateral) + 0.04;
    const scale = contactsTrunk
      ? 0.48 + random() * 0.34
      : 0.5 + random() * 0.62;
    dummy.position.set(base.x, surfaceHeight + scale * 0.16, base.z);
    dummy.rotation.set(0, random() * Math.PI, (random() - 0.5) * 0.12);
    dummy.scale.set(
      scale * (1.35 + random() * 0.72),
      scale * (contactsTrunk ? 0.24 : 0.31),
      scale * (0.82 + random() * 0.42),
    );
    dummy.updateMatrix();
    undergrowth.setMatrixAt(index, dummy.matrix);
  }
  undergrowth.instanceMatrix.needsUpdate = true;
  undergrowth.castShadow = false;
  undergrowth.receiveShadow = true;
  undergrowth.computeBoundingSphere();

  const groundStoneCount = CAPE_ASTER_ROUTE_STONE_PLACEMENTS.length + 24;
  const screeStoneCount = 64;
  const totalStoneCount = groundStoneCount + screeStoneCount;
  const stoneMeshes = stoneGeometries.map((geometry, variantIndex) => {
    const instanceCount = Math.floor(
      (totalStoneCount + stoneGeometries.length - 1 - variantIndex) /
        stoneGeometries.length,
    );
    const mesh = new THREE.InstancedMesh(
      geometry,
      stoneMaterial,
      instanceCount,
    );
    mesh.name = `Cape Aster asymmetric warm stone ${String(variantIndex + 1)}`;
    return mesh;
  });
  const defaultWarmStoneTint = new THREE.Color(0xd9b98d);
  const warmStoneTints = [
    defaultWarmStoneTint,
    new THREE.Color(0xe2c59b),
    new THREE.Color(0xead3ad),
    new THREE.Color(0xd3ad7f),
  ];
  const stoneColor = new THREE.Color();
  let stoneIndex = 0;
  const placeStone = (
    progress: number,
    lateral: number,
    surfaceHeight: number,
    scale: number,
    heightScale = 1,
  ): void => {
    const variantIndex = stoneIndex % stoneMeshes.length;
    const instanceIndex = Math.floor(stoneIndex / stoneMeshes.length);
    const mesh = stoneMeshes[variantIndex];
    if (mesh === undefined) {
      return;
    }
    track.sample(progress, 0, frame);
    base.copy(frame.center).addScaledVector(frame.right, lateral);
    dummy.position.set(base.x, surfaceHeight + scale * 0.045, base.z);
    dummy.rotation.set(
      (random() - 0.5) * 0.3,
      random() * Math.PI,
      (random() - 0.5) * 0.24,
    );
    dummy.scale.set(
      scale * (0.82 + random() * 0.48),
      scale * heightScale * (0.7 + random() * 0.24),
      scale * (0.84 + random() * 0.5),
    );
    dummy.updateMatrix();
    mesh.setMatrixAt(instanceIndex, dummy.matrix);
    stoneColor
      .copy(warmStoneTints[variantIndex] ?? defaultWarmStoneTint)
      .offsetHSL(
        (random() - 0.5) * 0.014,
        (random() - 0.5) * 0.045,
        (random() - 0.5) * 0.035,
      );
    mesh.setColorAt(instanceIndex, stoneColor);
    stoneIndex += 1;
  };

  for (const placement of CAPE_ASTER_ROUTE_STONE_PLACEMENTS) {
    track.sample(placement.progress, 0, frame);
    base.copy(frame.center).addScaledVector(frame.right, placement.lateral);
    const surfaceHeight =
      getCapeTerraceSurfaceHeight(frame, placement.lateral) + 0.02;
    placeStone(
      placement.progress,
      placement.lateral,
      surfaceHeight,
      placement.scale,
      placement.heightScale,
    );
  }

  const groundFillCount =
    groundStoneCount - CAPE_ASTER_ROUTE_STONE_PLACEMENTS.length;
  for (let index = 0; index < groundFillCount; index += 1) {
    const progress =
      CAPE_TERRACE_START +
      random() * (CAPE_TERRACE_END - CAPE_TERRACE_START);
    track.sample(progress, 0, frame);
    const lateral = -(13.2 + random() * 14.5);
    base.copy(frame.center).addScaledVector(frame.right, lateral);
    const surfaceHeight = getCapeTerraceSurfaceHeight(frame, lateral) + 0.02;
    placeStone(progress, lateral, surfaceHeight, 0.48 + random() * 0.72);
  }

  for (let index = 0; index < screeStoneCount; index += 1) {
    const progressFactor = (index + 0.35) / screeStoneCount;
    const progress =
      THREE.MathUtils.lerp(
        CAPE_TERRACE_START,
        CAPE_TERRACE_END,
        progressFactor,
      ) +
      (random() - 0.5) * 0.0024;
    const lateral =
      getCapeScreeCenterLateral(progress) + (random() - 0.5) * 6.3;
    track.sample(progress, 0, frame);
    base.copy(frame.center).addScaledVector(frame.right, lateral);
    const surfaceHeight = getCapeTerraceSurfaceHeight(frame, lateral) + 0.02;
    placeStone(progress, lateral, surfaceHeight, 0.34 + random() * 0.64);
  }

  for (const stoneMesh of stoneMeshes) {
    stoneMesh.instanceMatrix.needsUpdate = true;
    if (stoneMesh.instanceColor !== null) {
      stoneMesh.instanceColor.needsUpdate = true;
    }
    stoneMesh.castShadow = true;
    stoneMesh.receiveShadow = true;
    stoneMesh.computeBoundingSphere();
  }
  const screeApron = configureMesh(
    new THREE.Mesh(screeApronGeometry, stoneMaterial),
    false,
    true,
  );
  screeApron.name = "Cape Aster continuous photogrammetry scree apron";
  group.add(trunks, ...crowns, undergrowth, screeApron, ...stoneMeshes);
  return group;
};

const setVector3 = (
  target: THREE.Vector3,
  value: THREE.Vector3,
): boolean => {
  if (target.equals(value)) {
    return false;
  }
  target.copy(value);
  return true;
};

const SUNTRACE_SUN_OFFSET = new THREE.Vector3(280, 78, -175);
const SUNTRACE_FILL_OFFSET = new THREE.Vector3(-190, 112, 165);
const SUNTRACE_SUN_DIRECTION = SUNTRACE_SUN_OFFSET.clone().normalize();

export type SuntraceWorldLighting = {
  group: THREE.Group;
  hemisphere: THREE.HemisphereLight;
  sun: THREE.DirectionalLight;
  fill: THREE.DirectionalLight;
  target: THREE.Object3D;
  fillTarget: THREE.Object3D;
};

export const createSuntraceWorldLighting = (
  profile: SuntraceShadowProfile,
): SuntraceWorldLighting => {
  const group = new THREE.Group();
  group.name = "Coastal morning lighting";
  const hemisphere = new THREE.HemisphereLight(0x95b6c6, 0x3d4540, 0.36);
  const sun = new THREE.DirectionalLight(0xffd4a3, 2.1);
  const target = new THREE.Object3D();
  const fillTarget = new THREE.Object3D();
  sun.shadow.bias = -0.00018;
  sun.shadow.radius = 3;
  applySuntraceShadowProfile(sun, profile);
  sun.target = target;
  const fill = new THREE.DirectionalLight(0x77a7be, 0.22);
  fill.target = fillTarget;
  group.add(hemisphere, sun, target, fill, fillTarget);
  return { group, hemisphere, sun, fill, target, fillTarget };
};

export const updateSuntraceWorldLighting = (
  lighting: SuntraceWorldLighting,
  focus: THREE.Vector3,
  profile: SuntraceShadowProfile,
  shadowAnchor: THREE.Vector3,
  lightPosition: THREE.Vector3,
): void => {
  snapSuntraceShadowAnchor(
    focus,
    SUNTRACE_SUN_DIRECTION,
    profile,
    shadowAnchor,
  );
  const targetChanged = setVector3(lighting.target.position, shadowAnchor);
  lightPosition.copy(shadowAnchor).add(SUNTRACE_SUN_OFFSET);
  setVector3(lighting.sun.position, lightPosition);
  lightPosition.copy(focus).add(SUNTRACE_FILL_OFFSET);
  setVector3(lighting.fill.position, lightPosition);
  const fillTargetChanged = setVector3(lighting.fillTarget.position, focus);
  if (targetChanged) {
    lighting.sun.target.updateMatrixWorld();
  }
  if (fillTargetChanged) {
    lighting.fill.target.updateMatrixWorld();
  }
};

export const applySuntraceWorldShadowQuality = (
  lighting: Pick<SuntraceWorldLighting, "sun">,
  quality: SuntraceQuality,
): SuntraceShadowProfile => {
  const profile = createSuntraceShadowProfile(quality);
  applySuntraceShadowProfile(lighting.sun, profile);
  return profile;
};

export type SuntraceWorld = {
  group: THREE.Group;
  sun: THREE.DirectionalLight;
  update: (
    time: number,
    charge: number,
    focus: THREE.Vector3,
    colorGrade: SuntraceColorGrade,
    trackProgress: number,
  ) => void;
  getEnvironmentState: () => {
    weatheringEnabled: boolean;
    decalsVisible: boolean;
    authoredDensity: SuntraceAuthoredDensity;
  };
  setQuality: (quality: SuntraceQuality) => void;
  dispose: () => void;
};

export const createSuntraceWorld = (
  track: SuntraceTrack,
  materials: SuntraceMaterialLibrary,
  environmentAssets: SuntraceEnvironmentAssets | null = null,
): SuntraceWorld => {
  const group = new THREE.Group();
  group.name = "SUNTRACE Circuit Zero";
  const geometries: THREE.BufferGeometry[] = [];
  const localMaterials: THREE.Material[] = [];
  const localTextures: THREE.Texture[] = [];
  let shadowProfile = createSuntraceShadowProfile("high");
  const lighting = createSuntraceWorldLighting(shadowProfile);
  const authoredEnvironment = environmentAssets === null
    ? null
    : createSuntraceAuthoredEnvironment(
        track,
        environmentAssets,
        "high",
        getSuntraceRenderableSurfaceHeight,
        SUNTRACE_WORLD_PROP_GROUNDING_CONTRACT.minimumVisibleSurfaceHeight,
      );
  const environmentDecals = createSuntraceEnvironmentDecals(track);
  const capeHeroChapter = createCapeHeroChapter(
    track,
    materials,
    geometries,
    localMaterials,
    localTextures,
  );
  const asterHaloPetals = capeHeroChapter.getObjectByName(
    "Aster Halo kinetic petals",
  );
  group.add(
    createTerrain(track, materials, geometries, localMaterials),
    createRoad(track, materials, geometries, localMaterials),
    createGuardrails(track, materials, geometries),
    capeHeroChapter,
    createElevatedSupports(track, materials, geometries, localMaterials),
    createTempestTunnel(track, materials, geometries, localMaterials),
    createStartGantry(track, materials, geometries, localMaterials, localTextures),
    createLighthouse(track, materials, geometries, localMaterials),
    createMarshalPost(track, materials, geometries, localMaterials, localTextures),
    createVillage(track, materials, geometries, localMaterials),
    createAqueduct(materials, geometries),
    createHeliosSpire(materials, geometries, localMaterials),
    createVegetation(track, materials, geometries, localMaterials),
    environmentDecals.group,
    ...(authoredEnvironment === null ? [] : [authoredEnvironment.group]),
    lighting.group,
  );

  const shadowAnchor = new THREE.Vector3();
  const lightPosition = new THREE.Vector3();
  return {
    group,
    sun: lighting.sun,
    update(time, charge, focus, colorGrade, trackProgress) {
      materials.update(time, charge, colorGrade);
      authoredEnvironment?.updateVisibility(trackProgress);
      if (asterHaloPetals !== undefined) {
        asterHaloPetals.rotation.z = time * 0.16 + 0.12;
      }
      lighting.hemisphere.color.copy(colorGrade.hemisphereSkyColor);
      lighting.hemisphere.groundColor.copy(colorGrade.hemisphereGroundColor);
      lighting.hemisphere.intensity = colorGrade.hemisphereIntensity;
      lighting.sun.color.copy(colorGrade.sunColor);
      lighting.sun.intensity = colorGrade.sunIntensity;
      lighting.fill.color.copy(colorGrade.fillColor);
      lighting.fill.intensity = colorGrade.fillIntensity;
      updateSuntraceWorldLighting(
        lighting,
        focus,
        shadowProfile,
        shadowAnchor,
        lightPosition,
      );
    },
    getEnvironmentState() {
      return {
        weatheringEnabled:
          authoredEnvironment?.getWeatheringEnabled() ?? false,
        decalsVisible: environmentDecals.group.visible,
        authoredDensity: authoredEnvironment?.getActiveDensity() ?? {
          low: 0,
          medium: 0,
          high: 0,
          total: 0,
        },
      };
    },
    setQuality(quality) {
      shadowProfile = applySuntraceWorldShadowQuality(lighting, quality);
      authoredEnvironment?.setQuality(quality);
      environmentDecals.setQuality(quality);
    },
    dispose() {
      authoredEnvironment?.dispose();
      environmentDecals.dispose();
      for (const geometry of new Set(geometries)) {
        geometry.dispose();
      }
      for (const material of localMaterials) {
        material.dispose();
      }
      for (const texture of localTextures) {
        texture.dispose();
      }
      lighting.sun.shadow.map?.dispose();
      group.removeFromParent();
      group.clear();
    },
  };
};
